import type { LandingConfig } from "../components/SeoLandingPage";

// Free Kundli --------------------------------------------------------------
export const freeKundli: LandingConfig = {
  // SEO REBUILD, measured against the page that ranks first.
  // Srimandir /my-kundli: 2,278 words, "kundli" x76 (3.34% density), "online
  // kundli" x20, and 17 of its 40 headings carry the head term. Ours had the
  // same word count but "kundli" x34 (1.49%) and three headings. Length was
  // never the difference; head-term repetition, concentrated in headings, was.
  //
  // Two gaps they leave open, and both are where our clicks already are:
  //   * "kundali" (the -ali spelling) appears ONCE on their page. Queries using
  //     it earn us 514 clicks / 28 days: ai kundali 169, kundali ai 144,
  //     ai kundali free 120, plus prediction/reader/analysis variants.
  //   * they never mention AI. Every query we win is AI-prefixed.
  // So: match their density, and own the two terms they are not contesting.
  //
  // RETARGET 2026-10-12 (item 16): despite the note above, title and H1 still
  // led with plain "Free Kundli", a term Srimandir owns, and GSC showed this
  // page at position 31-54 on scraps while the HOMEPAGE held every ai-kundli
  // query at 4-7. Title/H1 now lead with "AI Kundli" (ai kundli 2.7K imps,
  // ai kundali 3.2K, kundali ai 2.4K, ai kundli free 1.8K / 28d), janam
  // kundli and date-of-birth phrasing kept in body.
  url: "https://astroaura.ai/free-kundli",
  title: "AI Kundli Free: Janam Kundli & Kundali by Date of Birth",
  description:
    "Free AI kundli in seconds: janam kundli (kundali) by date of birth with lagna, rashi, nakshatra, all 12 houses, dasha timeline and dosha checks, analysed by AI.",
  eyebrow: "",
  h1Pre: "AI",
  h1Accent: "Kundli",
  h1Post: ", Free Online",
  sub: "Generate your free AI kundli in seconds. Enter your date, time and place of birth for your full janam kundli (kundali): lagna, rashi, nakshatra, all twelve houses, your dasha timeline and every dosha, explained in plain language by AI.",
  primaryCta: "Get my free kundli",
  secondaryCta: "Ask about my kundli",
  articleSection: "Vedic astrology",
  ogImage: "/og-free-kundli.png",
  wide: true,
  hideHeroCtas: true,
  relatedLinks: [
    { to: "/kundli-matching", label: "kundli matching" },
    { to: "/manglik-calculator", label: "Manglik calculator" },
    { to: "/marriage/", label: "AI Marriage Prediction" },
    { to: "/nakshatra-calculator", label: "Find your janam nakshatra" },
    { to: "/moon-sign-calculator", label: "Find your moon sign (rashi)" },
    { to: "/blog/chatgpt-se-kundli", label: "ChatGPT se kundli: kya bharosa kar sakte hain?" },
  ],
  sections: [
    {
      h2: "What is a kundli (janam kundali)?",
      body: [
        "A kundli, also spelled kundali and often called a janam kundli or janam kundali, is a map of the sky at the exact moment you were born. Your kundli records where each of the nine grahas stood, which sign was rising on the eastern horizon, and which nakshatra the Moon occupied. In Vedic astrology it is the foundation of every reading, because every prediction is derived from the kundli.",
        "Three inputs produce a kundli: your date of birth, your time of birth and your place of birth. The date fixes the planets, the time fixes the ascendant and the houses, and the place fixes the horizon those houses are measured from. Change any one and the kundli changes with it.",
        "The two spellings mean the same thing. Kundli is the more common transliteration in the north, kundali is widely used elsewhere, and both describe the same Vedic birth chart. Whichever you searched for, this free online kundli builds the same chart.",
      ],
    },
    {
      h2: "What the AI adds to your kundli",
      body: [
        "A classic kundli website hands you the chart and leaves the reading to you. Here the kundli analysis is done by AI: it reads your lagna, the planets in each house and your running dasha together, and explains what they mean in plain language, in English or Hindi.",
        "That is the difference an AI kundli makes. Instead of looking up tables, you ask direct questions, will this year be better for work, when does my Shani dasha end, is this match workable, and the AI kundli reader answers from your own chart, not a generic sun-sign column.",
        "Predictions stay grounded in the classical rules: the AI kundli analysis applies the same Vedic principles an astrologer would, just faster, around the clock, and free to start.",
      ],
    },
    {
      h2: "Why your kundli matters",
      body: [
        "A kundli is not a fortune. It is a set of positions and the classical rules for reading them, and that is precisely what makes it useful: it gives you a structure for questions that otherwise have none.",
      ],
      subs: [
        {
          h3: "Understanding yourself",
          body: [
            "The lagna and the first house of your kundli describe temperament and how you come across, which is where most people find a reading starts to sound like them rather than like a horoscope column.",
          ],
        },
        {
          h3: "Career direction",
          body: [
            "The tenth house of the kundli, its lord and the dasha currently running are read together for work. A kundli will not name your job, but it describes the conditions you do your best work in.",
          ],
        },
        {
          h3: "Marriage and relationships",
          body: [
            "The seventh house, Venus and the Navamsa are read for partnership. When two people are being considered for marriage, both kundlis are compared rather than read alone.",
          ],
        },
        {
          h3: "Timing, through the dasha",
          body: [
            "A kundli says what is possible. The dasha sequence says when. This is the part most free kundli tools leave out, and it is usually the part that answers the question people actually came with.",
          ],
        },
      ],
    },
    {
      h2: "What information is required to create an online kundli?",
      body: [
        "Only three things, and the second one is the one people get wrong.",
      ],
      steps: [
        "Date of birth. This fixes where every graha stood in your kundli.",
        "Time of birth, as exactly as you can. This fixes the lagna, and with it all twelve houses of the kundli.",
        "Place of birth, picked from the list rather than typed, so the coordinates and time zone used to build your kundli are exact.",
      ],
      chips: [
        { to: "/blog/kundli-kaise-dekhe", label: "Kundli kaise dekhe" },
        { to: "/pitra-dosh", label: "Pitra dosh" },
        { to: "/guru-chandal-dosh", label: "Guru chandal dosh" },
        { to: "/kemdrum-dosh", label: "Kemdrum dosh" },
        { to: "/grahan-dosh", label: "Grahan dosh" },
        { to: "/kundli-matching", label: "Match two kundlis" },
        { to: "/manglik-calculator", label: "Check Mangal dosha" },
        { to: "/daily-horoscope", label: "Daily horoscope" },
      ],
    },
    {
      h2: "The 12 houses in your kundli",
      body: [
        "A kundli is divided into twelve houses, called bhavas. Each house governs a department of life, and the sign and planets sitting in that house of your kundli colour how that department runs.",
      ],
      table: {
        head: ["House", "Sanskrit name", "What it governs in your kundli"],
        rows: [
          ["1st", "Lagna / Tanu Bhava", "Self, body, temperament and how you come across"],
          ["2nd", "Dhana Bhava", "Money, possessions, speech and immediate family"],
          ["3rd", "Sahaja Bhava", "Courage, effort, siblings and communication"],
          ["4th", "Sukha Bhava", "Home, mother, property, vehicles and inner peace"],
          ["5th", "Putra Bhava", "Children, creativity, intelligence and past merit"],
          ["6th", "Ari Bhava", "Health, debt, competition, service and obstacles"],
          ["7th", "Yuvati Bhava", "Marriage, partnership and business alliances"],
          ["8th", "Ayur Bhava", "Longevity, upheaval, inheritance and hidden things"],
          ["9th", "Dharma Bhava", "Fortune, father, belief, higher learning and travel"],
          ["10th", "Karma Bhava", "Career, status, authority and public standing"],
          ["11th", "Labha Bhava", "Gains, income, networks and elder siblings"],
          ["12th", "Vyaya Bhava", "Expenditure, loss, sleep, foreign lands and moksha"],
        ],
        caption:
          "The twelve bhavas of a Vedic kundli and the area of life each one governs.",
      },
    },
    {
      h2: "Understanding planetary positions in your kundli",
      body: [
        "Vedic astrology reads nine grahas in a kundli, not the modern planets. Rahu and Ketu are the Moon's nodes rather than bodies, and Uranus, Neptune and Pluto do not appear in a classical kundali.",
      ],
      table: {
        head: ["Graha", "Also called", "What it signifies in a kundli"],
        rows: [
          ["Surya", "Sun", "Soul, father, authority, vitality and confidence"],
          ["Chandra", "Moon", "Mind, mother, emotions and temperament"],
          ["Mangal", "Mars", "Energy, courage, conflict, property and siblings"],
          ["Budha", "Mercury", "Intellect, speech, trade, analysis and learning"],
          ["Guru", "Jupiter", "Wisdom, wealth, children, teachers and belief"],
          ["Shukra", "Venus", "Love, marriage, comfort, art and pleasure"],
          ["Shani", "Saturn", "Discipline, delay, labour, longevity and karma"],
          ["Rahu", "North node", "Obsession, ambition, foreign things, sudden rise"],
          ["Ketu", "South node", "Detachment, past lives, insight and moksha"],
        ],
        caption: "The nine grahas read in a Vedic kundli and what each one carries.",
      },
    },
    {
      h2: "Key elements of a kundli: lagna, rashi and nakshatra",
      subs: [
        {
          h3: "Lagna, the foundation of your kundli",
          body: [
            "The lagna is the sign rising on the eastern horizon at the moment of birth, and it anchors the whole kundli: it decides which sign occupies which house, and therefore which graha rules what in your life.",
            "It moves roughly one sign every two hours, which is why the birth time matters so much in an online kundli. An hour's error hands you a different lagna and rearranges all twelve houses of the kundli with it.",
          ],
        },
        {
          h3: "Rashi, your Moon sign",
          body: [
            "Your rashi is the sign the Moon occupied at birth. In a Vedic kundli this, not the Sun sign, is what people usually mean by 'my sign', and it is what most predictive work and all kundli matching is read from. The Moon governs the mind, so the rashi in your kundali describes temperament more accurately than the Sun does.",
          ],
        },
        {
          h3: "Nakshatra, your birth star",
          body: [
            "The zodiac is also divided into 27 nakshatras, each a little over 13 degrees and each split into four padas. The nakshatra your Moon sat in is your birth star, and it carries a ruling planet, a symbol and a temperament of its own.",
            "Your nakshatra is the input for the dasha timeline in your kundli, for naming conventions, and for South Indian porutham matching. It is the finest of the three readings and often the most specific.",
          ],
        },
      ],
    },
    {
      h2: "Importance of dasha analysis in your kundli",
      body: [
        "A kundli says what is possible. The dasha says when, and a kundli read without it is only half a reading. The standard method is Vimshottari, a 120-year cycle divided among the nine grahas, and your starting point in it is set by the nakshatra your Moon occupied at birth.",
        "Each mahadasha runs for a fixed span, from Ketu's 7 years to Venus's 20, and each is subdivided into antardashas and further still. The graha running your current period colours what that stretch of life is about, which is why two people with similar kundlis can be in completely different seasons.",
        "This is the part most free kundli tools omit. A promising placement in your kundli that the current dasha is not activating frequently stays quiet until it is, and knowing that is usually more useful than the placement itself.",
      ],
    },
    {
      h2: "Doshas checked in your kundli",
      subs: [
        {
          h3: "Mangal dosha (Manglik) in a kundli",
          body: [
            "Mars placed in the 1st, 2nd, 4th, 7th, 8th or 12th house of the kundli. It is far more common than its reputation suggests, and classical Jyotish lists several conditions under which it is cancelled: both partners carrying it, Mars in its own sign or exaltation, or Mars aspected by Jupiter.",
          ],
        },
        {
          h3: "Kaal Sarp dosha",
          body: [
            "Formed when all seven main grahas in the kundli fall on one side of the Rahu-Ketu axis. It is read as a pattern of delay and struggle rather than a verdict, and there are twelve named variations depending on which houses of the kundali the axis occupies.",
          ],
        },
        {
          h3: "Pitra dosha",
          body: [
            "Read from afflictions to the 9th house and the Sun in the kundli, and associated in tradition with unresolved ancestral obligation. Remedies are ritual rather than gemstone-based.",
          ],
        },
        {
          h3: "Shani dosha and Sade Sati",
          body: [
            "Sade Sati is the seven-and-a-half year span while Saturn transits the sign before your Moon, your Moon, and the sign after. It is not a punishment: it is usually read as a period of consolidation that rewards patience and penalises shortcuts.",
          ],
        },
      ],
    },
    {
      h2: "How does a free online kundli work?",
      steps: [
        "Enter your date of birth, which fixes where every graha stood.",
        "Enter your time of birth as exactly as you can, which fixes the lagna of your kundli and all twelve houses.",
        "Enter your place of birth and pick it from the list, so the coordinates and time zone are exact rather than typed.",
        "Aura AI computes the kundli from precise astronomical data: planetary longitudes, the ascendant, house cusps and the nakshatra with its pada.",
        "It reads the kundli: lagna and rashi, grahas by house and sign, the Vimshottari dasha timeline and every dosha.",
        "Ask it anything about your own kundli in plain language, and get the reasoning rather than a verdict.",
      ],
    },
    {
      h2: "How accurate is an online kundli?",
      body: [
        "The arithmetic of a kundli is deterministic. Given a date, a time and a place, the planetary longitudes, the lagna, the house cusps and the dasha dates are a calculation, and software does not misadd them. An online kundli is therefore more reliable than one drawn by hand, and an AI kundli is no different in this respect: the chart is the chart.",
        "What differs between kundli tools is the reading, not the chart. A generator that draws the houses and stops leaves you no better off. What is useful is the cross-referencing: weighing every yoga, every aspect and the current dasha together, and showing the working rather than handing down a conclusion. A kundali AI does that part consistently; a human astrologer brings judgement and context that no software has. The sensible use is both.",
        "An online kundli is also there at two in the morning when the question actually occurs to you, which is when most people go looking for a free AI kundli in the first place.",
      ],
    },
    {
      h2: "Online kundli and a traditional kundli: the difference",
      body: [
        "A traditional kundli was drawn by hand from an ephemeris and a panchang, and the quality depended entirely on the care of the person doing it. Arithmetic slips were common, and two astrologers working from the same birth details could produce two different kundlis.",
        "An online kundli removes that source of error: the positions are computed from the same astronomical data professional software uses, and the same birth details always produce the same kundali. What an online kundli does not automatically bring is interpretation, which is why a free kundli generator that only draws the chart leaves you no better off than before.",
        "The useful combination is an accurate online kundli plus a reading that explains it, which is what this page is for.",
      ],
    },
    {
      h2: "How to read your kundli report",
      bullets: [
        "Start with the lagna, because it sets which sign rules which house of your kundli. Everything else is read against it.",
        "Find the Moon for your rashi and nakshatra, which carry temperament and set the dasha timeline.",
        "Read the house that matches your question: 10th for career, 7th for marriage, 2nd and 11th for money, 6th for health.",
        "Look at that house's lord and where it sits in the kundli, which often matters more than what is in the house itself.",
        "Check which dasha is running, because that is what decides whether a placement is active now or dormant.",
        "Only then look at the doshas, and read them with their cancellation rules rather than as verdicts.",
      ],
    },
    {
      h2: "Tips for an accurate online kundli",
      bullets: [
        "Use the birth certificate time if you have it. Family memory rounds to the nearest half hour, and half an hour can move the lagna.",
        "Pick the birth place from the list rather than typing it, so the kundli is built on real coordinates and the right time zone.",
        "For a birth abroad, make sure the place is the actual city, not the country: the kundli is computed from the horizon at that spot.",
        "If the time is genuinely unknown, use noon and treat the house-based parts of the kundli as provisional. The rashi and nakshatra still hold.",
        "Check the lagna against yourself. If the first-house description sounds nothing like you, the birth time is the first thing to question.",
      ],
    },
    {
      h2: "Free kundli for marriage (kundli milan)",
      body: [
        "A kundli read alone describes one person. For marriage, two kundlis are compared: the North Indian method is Ashtakoot gun milan, which scores eight kootas out of 36 gunas from both charts, and the South Indian method is porutham, read from the nakshatras.",
        "Both start from the same place you are now, which is an accurate kundli for each person. Build yours here, then match it.",
      ],
      chips: [
        { to: "/kundli-matching", label: "Kundli matching (gun milan)" },
        { to: "/thirumana-porutham/", label: "Thirumana porutham" },
      ],
    },
    {
      h2: "Why get your free kundli from Aura AI",
      bullets: [
        "Your kundli is computed from high-grade astronomical data, so the longitudes, the lagna and the nakshatra pada are exact.",
        "The full kundli: twelve houses, nine grahas, nakshatra with pada, and the Vimshottari dasha timeline with dates.",
        "Every major dosha checked, including the classical cancellation rules, so nothing in your kundali is reported as a problem when tradition says it is not.",
        "Explained in plain language, with the reasoning shown rather than a verdict handed down.",
        "Free to generate, private, and available whenever you need it.",
      ],
    },
  ],
  faqs: [
    {
      q: "Is the free kundli on Aura AI really free?",
      a: "Yes. The full kundli, lagna, planetary positions, Vimshottari dasha timeline and dosha checks, is free with no card required. It is computed from a real ephemeris using your exact birth date, time and place, the same calculation a careful jyotishi would start from.",
    },
    {
      q: "Is the AI kundli free?",
      a: "Yes, the AI kundli is free. Generating your full online kundli and asking your first question costs nothing and needs no card. After that, chats are paid from wallet credits, so you only pay for what you use. There is no subscription and nothing renews on you.",
    },
    {
      q: "How do I make a kundli online by date of birth?",
      a: "Enter your date of birth, your time of birth and your place of birth, and pick the place from the list so the coordinates and time zone are exact. Your free kundli is computed in seconds: lagna, rashi, nakshatra, all twelve houses, the dasha timeline and the dosha checks.",
    },
    {
      q: "What is the difference between kundli and kundali?",
      a: "Nothing. They are two transliterations of the same word: kundli is the more common spelling in the north, kundali is widely used elsewhere, and janam kundli and janam kundali both mean birth chart. This page builds the same chart whichever you searched for.",
    },
    {
      q: "Is an online kundli accurate?",
      a: "The calculation is exact: planetary positions, the lagna, house cusps and dasha dates are deterministic given correct birth details, so an accurate online kundli is simply a matter of correct inputs. Interpretation is where judgement enters, which is why this kundli shows its reasoning rather than only a conclusion.",
    },
    {
      q: "Can I get a kundli without the birth time?",
      a: "Yes, with a caveat. Your rashi and nakshatra come from the Moon, which moves slowly, so they stay reliable. The lagna and the twelve houses come from the time, so without it those parts of the kundli are provisional. Enter your best estimate rather than skipping it.",
    },
    {
      q: "What is my rashi, and is it my sun sign?",
      a: "In a Vedic kundli your rashi is your MOON sign, the sign the Moon occupied at birth. It is usually different from the sun sign used in Western horoscopes, and it is what most Vedic predictive work and all kundli matching is read from.",
    },
    {
      q: "How can I read my own kundli?",
      a: "Start with the lagna, because it sets which sign rules which house. Then find the Moon for your rashi and nakshatra. Then read the house that matches your question and its lord, and finally check which dasha is running, because that is what says when.",
    },
    {
      q: "What is the difference between a kundli and a horoscope?",
      a: "A kundli is your own birth chart, calculated from your date, time and place of birth. A horoscope in the everyday sense is a general forecast written for a whole sun sign. One is personal and fixed; the other is generic and daily.",
    },
    {
      q: "Is my birth data private?",
      a: "Yes. Your birth details are used only to compute your kundli and are never sold.",
    },
  ],
  schemaName: "Free Kundli Online - Aura AI",
  schemaDescription:
    "Free online kundli and janam kundali: full Vedic birth chart with lagna, rashi, nakshatra, twelve houses, nine grahas, Vimshottari dasha timeline and dosha checks.",
  finalHeading: "Get your free kundli, then ask what it means",
  finalText:
    "Generate your full kundli free, then ask anything about your chart. The first question costs nothing.",
  disclaimer:
    "A kundli is a tradition of interpretation, not a prediction of any outcome. Nothing here is a substitute for your own judgement, or for professional advice on health, legal or financial matters.",
};

// Kundli Matching ----------------------------------------------------------
// REBUILT for the gun milan cluster. The old version was 1,258 words against
// Srimandir's 2,444 and Astrotalk's 2,924, and it earned 29 clicks in 28 days
// at position 7.9. It ranked for "ai marriage prediction" phrasing and was
// absent from the actual volume: kundli matching, gun milan, kundali milan,
// guna milan, horoscope matching. Every H2 below targets a query we scored
// zero on. The koota table is the featured-snippet play; neither competitor
// pairs a table with FAQPage + HowTo schema, and we already emit both.
export const kundliMatching: LandingConfig = {
  url: "https://astroaura.ai/kundli-matching",
  title: "Kundli Matching for Marriage: Free Gun Milan by Date of Birth",
  description:
    "Free kundli matching for marriage. Get your Ashtakoot gun milan score out of 36, all 8 kootas explained, plus Mangal, Nadi and Bhakoot dosha checks.",
  eyebrow: "",
  h1Pre: "Free",
  h1Accent: "Kundli Matching",
  h1Post: " for Marriage",
  sub: "Check marriage compatibility the Vedic way. Enter both sets of birth details for an instant gun milan score out of 36 across all eight kootas, with Mangal, Nadi and Bhakoot dosha checked in both charts.",
  primaryCta: "Match kundli free",
  secondaryCta: "Ask about compatibility",
  articleSection: "Vedic astrology",
  ogImage: "/og-kundli-matching.png",
  wide: true,
  // The tool is directly below the hero, so a hero button scrolled to
  // something already on screen while pushing the copy that ranks off the fold.
  hideHeroCtas: true,
  // ONE paragraph, not three. Three ran as two CSS columns and the columns
  // came out ragged: the last paragraph ended halfway up the right column and
  // left a hole. Astrotalk's intro is a single justified block of four lines
  // and that is precisely why it reads clean. The material cut from here was
  // duplicating the first section below anyway.
  relatedLinks: [
    { to: "/thirumana-porutham/", label: "thirumana porutham" },
    { to: "/manglik-calculator", label: "Manglik calculator" },
    { to: "/love-calculator", label: "love calculator" },
    { to: "/marriage/", label: "AI marriage prediction" },
    { to: "/blog/kundli-matching-gun-milan-explained", label: "How the 36 Gun Milan score works" },
    { to: "/blog/kundli-for-nris", label: "Kundli matching for NRI couples" },
  ],
  sections: [
    {
      h2: "What is kundli matching (kundli milan)?",
      body: [
        "Kundli matching, also called kundli milan, gun milan or horoscope matching, is the Vedic practice of comparing two birth charts before marriage to judge how well the couple is likely to suit each other. In North India it is often called patrika matching; in the South, poruthams. All of these describe the same underlying idea: read both charts side by side rather than one alone.",
        "The classical method is Ashtakoot, meaning eight kootas or eight categories. Each koota measures a different dimension of married life, from temperament to health to money, and each carries a fixed weight. Added together they come to 36 points, and those points are the gunas people mean when they ask how many gunas matched.",
        "Almost all of Ashtakoot is read from the Moon: specifically each person's Moon sign (rashi) and the nakshatra the Moon occupied at birth. That is deliberate. In Jyotish the Moon governs the mind, the emotions and temperament, and a marriage is lived mostly in those registers rather than in the ones the Sun rules.",
        "One thing worth being honest about at the start: the score is a summary, not a verdict. It compresses eight quite different measurements into a single number, and two couples with an identical 24 can have completely different reasons for it. The number tells you where to look. It does not tell you what to do.",
      ],
    },
    {
      h2: "Why kundli matching matters before marriage",
      body: [
        "Ashtakoot is not one test. It is eight, grouped so that between them they cover the areas where couples actually struggle.",
      ],
      subs: [
        {
          h3: "Emotional compatibility (Gana and Graha Maitri)",
          body: [
            "Gana sorts each nakshatra into one of three temperaments: Deva (gentle, accommodating), Manushya (balanced, practical) and Rakshasa (forceful, strong-willed). It carries 6 points because a persistent mismatch in temperament is the friction couples feel every single day, not once a year.",
            "Graha Maitri, worth 5 points, looks at whether the planets ruling the two Moon signs are natural friends. It is the closest thing in the system to a measure of whether two people simply understand each other, and it tends to matter more over decades than any other koota.",
          ],
        },
        {
          h3: "Health and longevity",
          body: [
            "Tara (3 points) counts the distance between the two birth nakshatras, and classical texts read some of those distances as inauspicious for well-being. Nadi (8 points) is the heaviest koota in the entire system and is treated as a constitutional match rather than a personality one.",
          ],
        },
        {
          h3: "Financial harmony and family life",
          body: [
            "Bhakoot, at 7 points, is the second-heaviest koota. It compares the two Moon signs by their distance from one another and is read for prosperity, family welfare and the overall direction of the household after marriage.",
          ],
        },
        {
          h3: "Children and progeny",
          body: [
            "Nadi is scored for genetic and constitutional compatibility, and traditionally it is read with children in mind. It is the reason a Nadi mismatch is taken seriously even when the total score looks respectable, and the reason it is worth understanding rather than fearing.",
          ],
        },
      ],
    },
    {
      h2: "How kundli matching works, step by step",
      steps: [
        "Enter the date, exact time and place of birth for both partners. Time and place matter because they fix the Moon's position, and the Moon is what Ashtakoot reads.",
        "Aura AI computes both janam kundlis from precise astronomical data, including each person's Moon sign and birth nakshatra.",
        "It scores all eight kootas in turn, from Varna at 1 point up to Nadi at 8, and totals them into a gun milan score out of 36.",
        "It checks Mangal dosha in both charts, and whether any of the classical cancellation rules apply.",
        "It checks Nadi and Bhakoot dosha, including the standard exceptions that set them aside.",
        "It reads the wider chart context that a score cannot hold: the 7th house of marriage, the Navamsa (D9) chart, and the dasha periods both people are currently running.",
      ],
      chips: [
        { to: "/blog/kitne-gun-milne-chahiye", label: "Kitne gun milne chahiye" },
        { to: "/nadi-dosh", label: "Nadi dosh" },
        { to: "/free-kundli", label: "Build a free kundli first" },
        { to: "/manglik-calculator", label: "Check Manglik status" },
        { to: "/love-calculator", label: "Love compatibility" },
        { to: "/daily-horoscope", label: "Daily horoscope" },
      ],
    },
    {
      h2: "The 8 kootas and all 36 guna points",
      body: [
        "Every koota carries a fixed maximum. A pair either earns the full weight, a partial share or nothing at all, and the eight maximums add up to 36.",
      ],
      table: {
        head: ["Koota", "Points", "What it measures"],
        rows: [
          ["Varna", "1", "Work ethic and ego; the broad temperamental caste of each partner"],
          ["Vashya", "2", "Mutual attraction, and the natural balance of influence between the two"],
          ["Tara", "3", "Health, fortune and well-being, counted from birth nakshatra to birth nakshatra"],
          ["Yoni", "4", "Physical and intimate compatibility, read through the nakshatra's animal symbol"],
          ["Graha Maitri", "5", "Mental and emotional rapport, from the friendship of the two Moon-sign lords"],
          ["Gana", "6", "Temperament: Deva, Manushya or Rakshasa nature"],
          ["Bhakoot", "7", "Family welfare, prosperity and the direction of life together"],
          ["Nadi", "8", "Constitutional and genetic compatibility, read with health and children in mind"],
        ],
        caption:
          "Ashtakoot gun milan: the eight kootas and their maximum points, totalling 36.",
      },
    },
    {
      h2: "What is a good gun milan score out of 36?",
      body: [
        "Because Nadi and Bhakoot alone are worth 15 of the 36 points, two otherwise well-matched charts can lose a large block of score on a single factor. That is why the band matters more than the exact number, and why the reason behind the number matters more than either.",
      ],
      cards: [
        {
          label: "Below 18",
          title: "Traditionally not advised",
          body: "Classical texts treat under 18 as an insufficient match. In practice it is a reason to look closely at which kootas failed rather than to stop, because a low total driven by one heavy koota reads very differently from one spread across all eight.",
          tone: "bad",
        },
        {
          label: "18 to 24",
          title: "Acceptable",
          body: "The most common band, and the usual threshold for going ahead. The marriage is considered workable, with specific areas that will need attention.",
          tone: "ok",
        },
        {
          label: "25 to 32",
          title: "Good to very good",
          body: "A strong match on most kootas. Any remaining dosha is usually manageable and worth understanding rather than worrying about.",
          tone: "good",
        },
        {
          label: "33 to 36",
          title: "Excellent",
          body: "Rare, and not something to hold out for. A very high score is a good sign, but it never removes the need to look at the 7th house, the Navamsa and the dashas.",
          tone: "best",
        },
      ],
      subs: [
        {
          h3: "Can you marry with a low kundli matching score?",
          body: [
            "Yes, and many people do. A low total is a prompt to find out which koota caused it. Losing 8 points to Nadi is a different conversation from losing 1 to Varna, even though the second looks better on paper only by a single point.",
            "It is also worth remembering what Ashtakoot does not look at. It says nothing about the 7th house, which governs marriage directly, nothing about the Navamsa chart, which is read for the durability of a marriage, and nothing about which dasha period either person is running. A careful reading covers all of these. A score out of 36 covers none of them.",
          ],
        },
      ],
    },
    {
      h2: "Mangal dosha (Manglik) in kundli matching",
      body: [
        "Mangal dosha, commonly called being Manglik, is the placement of Mars in the 1st, 2nd, 4th, 7th, 8th or 12th house. Some traditions count these houses from the ascendant only; others also check from the Moon and from Venus, which is why one site can call you Manglik and another may not.",
        "It is far more common than its reputation suggests, and it is not a verdict on anyone's marriage.",
      ],
      subs: [
        {
          h3: "Anshik (partial) versus Sampurna (full) Manglik",
          body: [
            "Anshik Manglik means the dosha is present but weakened, typically because Mars sits in a sign where it is not comfortable expressing itself, or because it is aspected by a benefic. Sampurna Manglik means the placement is unmitigated. The distinction matters, because most people described simply as Manglik are the partial kind.",
          ],
        },
        {
          h3: "When Mangal dosha is cancelled",
          body: [
            "Classical Jyotish lists several conditions under which the dosha is considered cancelled or neutralised (Mangal dosha bhanga). The most widely used: both partners have the dosha, in which case it is treated as balanced; Mars sits in its own sign or in exaltation; Mars is aspected by or conjunct Jupiter; or Mars occupies certain signs where the placement loses its force.",
            "This is why checking the two charts together is the only meaningful way to read Mangal dosha. Reading one chart alone tells you almost nothing about the marriage.",
          ],
        },
        {
          h3: "Remedies",
          body: [
            "Where the dosha is genuinely active, the traditional responses are simple and low-cost: Hanuman worship, the recitation of the Hanuman Chalisa, observing Tuesday fasts, and charitable acts. Aura AI tells you whether any remedy is warranted at all, rather than prescribing one by default.",
          ],
        },
      ],
    },
    {
      h2: "Nadi dosha and Bhakoot dosha",
      subs: [
        {
          h3: "Nadi dosha",
          body: [
            "Each of the 27 nakshatras belongs to one of three nadis: Adi, Madhya or Antya. When both partners share the same nadi, the koota scores zero, and because Nadi carries 8 points that single result can move a total from comfortable to concerning on its own.",
            "There are recognised exceptions. The dosha is generally set aside when both partners share the same Moon sign but different nakshatras, when they share a nakshatra but occupy different padas, or when the Moon-sign lords are the same planet.",
          ],
        },
        {
          h3: "Bhakoot dosha",
          body: [
            "Bhakoot compares the two Moon signs by the distance between them. Three distances are traditionally read as doshas: 6 and 8 (Shadashtak), 2 and 12 (Dwirdwadash), and 5 and 9 (Navpancham). When one applies, the koota scores zero and 7 points are lost.",
            "As with Nadi, exceptions exist, most commonly where the lords of the two Moon signs are the same planet or are natural friends.",
          ],
        },
      ],
    },
    {
      h2: "Does kundli matching work for love marriages?",
      body: [
        "It is used for both, but the purpose shifts. In an arranged match, gun milan is often a screening step taken before the couple know each other well. In a love marriage, the couple already have the information the score is trying to estimate, so it reads less like a decision and more like a map.",
        "Used that way it is genuinely useful. A chart that flags Gana mismatch is describing something a couple usually recognises immediately, and knowing it is structural rather than personal tends to make it easier to handle, not harder.",
      ],
    },
    {
      h2: "Kundli matching by date of birth versus by name",
      body: [
        "Matching by date of birth is the accurate method, because the birth date, time and place are what fix the Moon's position, and the Moon is what the whole Ashtakoot system reads.",
        "Name-based matching is a fallback for when birth details are unknown. It derives a likely nakshatra from the first syllable of each name, which is a real traditional convention, but it is an approximation of one input rather than a measurement of it. If you have both birth dates, use them.",
        "If you do not know a birth time, enter your best estimate rather than skipping the match. Most of the Ashtakoot score depends on the Moon's sign and nakshatra, which usually do not change across a few hours, so the total is often stable even when the ascendant is not.",
      ],
    },
    {
      h2: "North Indian and South Indian matching are not the same system",
      chips: [
        { to: "/thirumana-porutham/", label: "South Indian: thirumana porutham" },
      ],
      body: [
        "The 36-guna Ashtakoot method described on this page is the North Indian standard, and it is what most people mean by kundli matching or gun milan.",
        "Much of South India uses a different framework. Tamil tradition matches on ten poruthams, and Kerala and other regions use variants that weight the factors differently, notably giving Rajju, which is read for the longevity of the marriage, a prominence it does not have in Ashtakoot.",
        "The two systems can reach different conclusions about the same couple. Neither is a broken version of the other; they are separate traditions with separate priorities, and it is normal for a family to follow the one native to their region.",
      ],
    },
    {
      h2: "What to do if the kundli matching score is low",
      bullets: [
        "Find out which koota failed. Losing 8 points on Nadi and losing 8 across six small kootas are entirely different situations with the same total.",
        "Check whether an exception applies. Nadi and Bhakoot both have well-established conditions that set the dosha aside, and they are missed surprisingly often.",
        "Read the 7th house and the Navamsa (D9) chart. Ashtakoot ignores both, and both speak to marriage more directly than several of the kootas do.",
        "Look at the dasha periods each person is running, which govern timing rather than compatibility.",
        "Take remedies proportionately. They are worth doing where a dosha is genuinely active, and worth skipping where it is not.",
      ],
    },
    {
      h2: "Why match your kundli with Aura AI",
      bullets: [
        "The full Ashtakoot calculation across all eight kootas, with the points for each one shown rather than hidden behind a total.",
        "Mangal, Nadi and Bhakoot dosha checked in both charts together, including the cancellation rules, which is the only way those doshas mean anything.",
        "The context a score leaves out: 7th house, Navamsa (D9) and current dasha periods.",
        "A plain-language reading of what the result means for the two of you, and the chance to ask follow-up questions about your own charts.",
        "Free to run, private, and available whenever you need it.",
      ],
    },
  ],
  faqs: [
    {
      q: "How many gunas should match for a good marriage?",
      a: "Out of 36 gunas, 18 or more is traditionally considered acceptable, 24 and above a good match, and 32 or above excellent. The score alone is not the verdict: Nadi and Bhakoot doshas must be checked along with their classical cancellations, which Aura AI's kundli matching does automatically and explains in plain words.",
    },
    {
      q: "What is the minimum gun milan score for marriage?",
      a: "Traditionally 18 out of 36 is the accepted minimum, and higher is better. Below 18 is classically read as insufficient. In practice the reason behind the number matters more than the number: a total pulled down by one heavy koota such as Nadi reads very differently from one that is weak across all eight.",
    },
    {
      q: "Is kundli matching free on Aura AI?",
      a: "Yes. You can run a full Ashtakoot gun milan for two charts and ask your first question free, with no card required. Detailed written reports are available on affordable plans.",
    },
    {
      q: "Can a Manglik marry a non-Manglik?",
      a: "Yes, and it is common. Classical Jyotish recognises several conditions that cancel Mangal dosha, including Mars sitting in its own sign or in exaltation, Mars being aspected by Jupiter, and both partners carrying the dosha. Aura AI checks both charts for these before reporting anything as a problem.",
    },
    {
      q: "What is Nadi dosha and how serious is it?",
      a: "Nadi dosha occurs when both partners share the same nadi (Adi, Madhya or Antya), which scores zero on the heaviest koota in the system and costs 8 points. It is taken seriously, but there are recognised exceptions, including the same Moon sign with different nakshatras, or the same nakshatra with different padas.",
    },
    {
      q: "What is Bhakoot dosha?",
      a: "Bhakoot dosha applies when the two Moon signs sit at particular distances from each other: 6 and 8, 2 and 12, or 5 and 9. It costs the full 7 points of the Bhakoot koota. Exceptions apply, most often when the lords of the two Moon signs are the same planet or natural friends.",
    },
    {
      q: "Do I need exact birth times for both people?",
      a: "Exact times give the most accurate result, particularly for Mangal dosha, which depends on the ascendant. But most of the Ashtakoot score comes from the Moon's sign and nakshatra, which usually hold steady across a few hours, so a good estimate still produces a useful match you can refine later.",
    },
    {
      q: "How accurate is online kundli matching?",
      a: "The arithmetic is exact: given correct birth details, the eight kootas are a deterministic calculation and a computer will not misadd them. The interpretation is where judgement enters. Aura AI shows the score for each koota and its reasoning, so you can see what drove the result rather than being handed a verdict.",
    },
    {
      q: "Does kundli matching work for love marriages?",
      a: "It is used for both. In a love marriage the couple already know much of what the score estimates, so it works better as a map of where friction is likely than as a decision about whether to proceed.",
    },
    {
      q: "Is kundli matching the same as horoscope matching or patrika matching?",
      a: "Yes. Kundli milan, gun milan, guna milan, horoscope matching and patrika matching all describe the same Ashtakoot process. South Indian traditions use a different system based on ten poruthams, which can reach a different conclusion for the same couple.",
    },
    {
      q: "Is our birth data private?",
      a: "Yes. Birth details are used only to compute the charts and the match. They are never sold.",
    },
  ],
  schemaName: "Kundli Matching - Aura AI",
  schemaDescription:
    "Free kundli matching and gun milan for marriage: full Ashtakoot analysis across all 8 kootas out of 36 gunas, with Mangal, Nadi and Bhakoot dosha checks explained by AI.",
  finalHeading: "Match the charts, then ask what it means",
  finalText:
    "Run the full gun milan free, then ask Aura AI what your score actually means for the two of you. The first question costs nothing.",
  disclaimer:
    "Kundli matching is a tradition of interpretation, not a prediction of any outcome. Nothing here is a substitute for your own judgement, or for professional advice on health, legal or financial matters.",
};

// Daily Horoscope ----------------------------------------------------------
// RETARGET 2026-10-13 (item 17): generic "daily horoscope" is a publisher-owned
// head term we never ranked for; GSC 28d showed this page earning only brand
// sitelinks while the HOMEPAGE held the whole AI-prefixed cluster (ai horoscope
// free 25 clicks @5.9 w/ 5.2% CTR, ai horoscope 78 @5.3, horoscope ai 93 @4.0,
// ai horoscope prediction free @4.2, ai horoscope by date of birth @4.3). The
// page's own best signal was "daily horoscope ai" at 28.6% CTR. This page now
// owns "AI Horoscope Free" + daily/prediction/date-of-birth modifiers;
// /horoscope/ keeps "today's horoscope", homepage keeps the broad astrology head.
export const dailyHoroscope: LandingConfig = {
  url: "https://astroaura.ai/daily-horoscope",
  title: "AI Horoscope Free: Daily Predictions by Date of Birth | Aura AI",
  description:
    "Free AI horoscope, every day. Predictions by date of birth from your own birth chart, not a generic sun-sign column: love, career, money and health, in English or Hindi.",
  eyebrow: "Daily Horoscope, personalised",
  h1Pre: "AI",
  h1Accent: "Horoscope",
  h1Post: ", Free Daily",
  sub: "A free AI horoscope generated from your date of birth, every day. Aura AI reads today's planetary movements against your own kundli, so the prediction is about your chart, not your whole sun sign.",
  primaryCta: "Get today's horoscope",
  secondaryCta: "Ask about your day",
  sections: [
    {
      h2: "What makes an AI horoscope different",
      body: [
        "Most horoscopes are written once for a whole sun sign, so they say the same thing to millions of people. An AI horoscope starts from your date, time and place of birth instead: Aura AI computes your chart, then reads today's transits against your own planets, dashas and timing.",
        "That is why the same day reads differently for two people with the same sun sign. The free AI horoscope you get here is a prediction about your chart, not a paragraph recycled for one twelfth of the world.",
      ],
    },
    {
      h2: "Daily horoscope for all 12 zodiac signs",
      bullets: [
        "Aries, Taurus, Gemini and Cancer",
        "Leo, Virgo, Libra and Scorpio",
        "Sagittarius, Capricorn, Aquarius and Pisces",
        "Plus a Vedic Moon-sign (rashi) reading, the sign Indian astrology relies on most.",
      ],
    },
    {
      h2: "What your daily reading covers",
      bullets: [
        "Love and relationships: the mood of your day with people who matter.",
        "Career and work: where to push and where to hold back.",
        "Money and finances: timing for decisions, spends and opportunities.",
        "Health and energy: how to spend your energy well today.",
      ],
    },
    {
      h2: "Why AI daily predictions",
      bullets: [
        "Reads 100+ astrological variables at once for a sharper daily view.",
        "Learns from millions of charts and keeps getting more accurate.",
        "Shows the reasoning behind each forecast, so you are not just told, you understand.",
        "Available 24x7, in English and Hindi, free to start.",
      ],
    },
  ],
  faqs: [
    {
      q: "Is the AI horoscope free?",
      a: "Yes. You can read your personalised AI horoscope every day and ask your first question free.",
    },
    {
      q: "How do I get my AI horoscope by date of birth?",
      a: "Enter your date, time and place of birth once. Aura AI computes your kundli from them and generates your horoscope from that chart every day after, no repeat entry needed.",
    },
    {
      q: "How is this different from a normal horoscope?",
      a: "A normal horoscope is written for a whole sun sign. Aura AI personalises your reading to your own birth chart and today's transits, so it reflects your unique planetary positions rather than a generic forecast.",
    },
    {
      q: "Which zodiac sign should I read, sun or moon?",
      a: "In Vedic astrology your Moon sign (rashi) usually matters most for daily predictions, but Aura AI can read both. Give your birth details and it uses the right one for you.",
    },
    {
      q: "Is it based on Vedic astrology?",
      a: "Yes. Your daily horoscope is grounded in Vedic (Jyotish) principles, using your nakshatra, dashas and today's planetary transits.",
    },
  ],
  schemaName: "AI Horoscope Free - Aura AI",
  schemaDescription:
    "Free AI horoscope by date of birth, daily. Personalised to your own Vedic birth chart, covering love, career, money and health.",
  finalHeading: "Read your free AI horoscope",
  finalText:
    "A daily prediction from your own chart, free, in English or Hindi.",
  relatedLinks: [{ to: "/hi", label: "Hindi mein rashifal padhein" }],
};

// Talk to Astrologer -------------------------------------------------------
export const talkToAstrologer: LandingConfig = {
  url: "https://astroaura.ai/talk-to-astrologer",
  title: "Talk to an Astrologer Online, Free: 24x7 AI Chat | Aura AI",
  description:
    "Talk to an astrologer online, free to start: Aura AI is a private AI Vedic astrologer with clear answers on career, love, marriage, money and timing, 24x7.",
  eyebrow: "Talk to an Astrologer, 24x7",
  h1Pre: "Talk to an",
  h1Accent: "AI Astrologer",
  h1Post: ", 24x7",
  sub: "No appointment, no per-minute meter, no judgment. Chat with Aura AI, your personal AI Vedic astrologer, and get clear answers on career, love, marriage, money and timing, any time of day.",
  primaryCta: "Start chatting free",
  secondaryCta: "Get your free kundli",
  sections: [
    {
      h2: "Talk to an astrologer online, any time",
      body: [
        "Choosing the right astrologer is confusing, and calls often come with a meter running. Aura AI removes all of that. It combines thousands of classical Jyotish rules, 1M+ historical charts and advanced AI into one astrologer you can talk to whenever you need, in English or Hindi.",
        "Ask a real question and get a clear, personalised answer rooted in your own kundli, not a vague horoscope line.",
      ],
    },
    {
      h2: "What you can ask",
      bullets: [
        "When will my career or business finally take off?",
        "What does my kundli say about marriage, love and compatibility?",
        "Which mahadasha am I in, and what does it mean for me now?",
        "Is this the right time for a big decision, a move or an investment?",
        "What remedies can strengthen a weak planet in my chart?",
      ],
    },
    {
      h2: "Safe, private and judgment-free",
      body: [
        "Some questions are hard to ask a stranger. Aura AI gives you a space to speak freely.",
      ],
      bullets: [
        "Safe. Private. Encrypted. A space to speak without judgment.",
        "No per-minute pricing on chat, and no hidden fees.",
        "Available 24x7, whether it is midnight in Mumbai or dawn in Chicago.",
      ],
    },
    {
      h2: "AI astrologer vs a human astrologer",
      body: [
        "A good human astrologer brings intuition and years of experience. An AI astrologer brings speed, consistency and availability: the same careful reading every time, no waiting and no rising fees. Aura AI gives you the everyday clarity of a trusted astrologer in your pocket, and points you deeper whenever you want.",
      ],
    },
  ],
  faqs: [
    {
      q: "Can I talk to an astrologer online for free?",
      a: "On Aura AI, yes, to start: your first question to the AI astrologer is free, with no card and no per-minute meter. After that, chats are paid from small wallet credits, and the AI reads your actual birth chart before answering, so the guidance is about your chart, not your sun sign.",
    },
    {
      q: "Is it free to talk to the astrologer?",
      a: "Yes. Your first question is free, no card is required, and chat is never billed per minute. After that, each chat is paid from your wallet credits, so you only pay for what you actually use, and nothing auto-renews on its own. If you consult every week, an optional subscription on the pricing page works out cheaper per reading.",
    },
    {
      q: "Is the chat private?",
      a: "Yes. Your conversations are private and encrypted, and your birth data is never sold. It is a judgment-free space to ask anything.",
    },
    {
      q: "Is it a real astrologer or a bot?",
      a: "Aura AI is an AI Vedic astrologer: it reads your actual birth chart using classical Jyotish principles and 1M+ real charts. It is far more than a generic chatbot.",
    },
    {
      q: "Can I chat in Hindi?",
      a: "Yes. You can talk to Aura AI in English or Hindi and get answers in the language you are most comfortable with.",
    },
  ],
  schemaName: "Talk to an Astrologer - Aura AI",
  schemaDescription:
    "Talk to an astrologer online, free and 24x7. Chat with Aura AI, a private AI Vedic astrologer, for personalised answers on career, love, marriage, money and timing.",
  finalHeading: "Talk to your astrologer now",
  finalText:
    "Your first question is free, private and answered in seconds, 24x7.",
};

// Methodology / trust page -------------------------------------------------
//
// BUILD 2026-10-14 (item 24): the E-E-A-T page the audit asked for. States
// how a reading is produced (exact astronomy, then classical Jyotish rules,
// then an AI language layer), what the product does NOT claim, and the YMYL
// disclaimer. Every statement here is checkable against the product. The
// site-wide claim cleanup (item 3) removed invented ratings and headcounts,
// so this page must never reintroduce numbers of that kind. The
// named-astrologer reviewer block (also item 24) is pending a real reviewer
// from the founder and must not be invented.
export const methodology: LandingConfig = {
  url: "https://astroaura.ai/methodology",
  title: "Our Methodology: How Aura AI Reads a Chart | Aura AI",
  description:
    "How Aura AI works: exact Swiss Ephemeris astronomy, classical Vedic (Jyotish) rules, and AI that writes the answer in plain language, with honest limits.",
  eyebrow: "Methodology and sources",
  h1Pre: "How Aura AI",
  h1Accent: "Reads a Chart",
  sub: "Every answer starts with exact astronomy, passes through classical Vedic rules, and only then becomes plain language. This page explains each step, what we do not claim, and how to use astrological guidance responsibly.",
  primaryCta: "Get your free kundli",
  secondaryCta: "Ask your first question free",
  sections: [
    {
      h2: "Step 1: exact astronomy",
      body: [
        "Your date, time and place of birth go into the Swiss Ephemeris, the same astronomical dataset professional astrologers use. From it Aura AI computes the positions of the planets at your birth, your ascendant, the twelve houses, your Moon nakshatra and your Vimshottari dasha timeline.",
        "This layer is deterministic. The same birth details always produce the same chart, and anyone can verify the planetary positions against any other Swiss Ephemeris tool.",
      ],
    },
    {
      h2: "Step 2: classical Jyotish rules",
      body: [
        "Interpretation follows the structure of classical Vedic astrology: house significations, planetary lordships and aspects, yogas, and the timing framework of mahadashas and antardashas. The AI applies those established rules to your specific chart; it does not invent new astrology.",
        "Where the tradition itself offers more than one reading of a combination, the answer says so instead of pretending certainty.",
      ],
    },
    {
      h2: "Step 3: plain language",
      body: [
        "The last layer is language. Aura AI writes the interpretation as a clear answer to the question you actually asked, in English or Hindi, instead of a list of technical placements. You can always ask it to show the chart factors behind an answer.",
      ],
    },
    {
      h2: "What we do not claim",
      bullets: [
        "We do not claim astrology is scientifically proven. The astronomy is exact; the meaning comes from the Jyotish tradition.",
        "We do not quote accuracy percentages, and we do not promise outcomes.",
        "We do not give medical, legal or financial advice, and a reading is not a substitute for a qualified professional.",
        "We do not sell your birth data. It is used to compute your chart.",
      ],
    },
    {
      h2: "Review and corrections",
      body: [
        "This page and the astrology content on the site are maintained by the Aura AI team. If you find a statement that misrepresents the Jyotish tradition or the product, write to us through the contact page and we will check it against the classical sources and correct it.",
      ],
    },
  ],
  faqs: [
    {
      q: "Which astrology system does Aura AI use?",
      a: "Vedic astrology (Jyotish): Moon sign, nakshatras, dashas and divisional charts, computed from Swiss Ephemeris astronomy. It is built for Indian users but works for anyone born anywhere.",
    },
    {
      q: "Is astrology scientifically proven?",
      a: "No, and we do not claim it is. The planetary positions are exact, verifiable astronomy; the meaning assigned to them comes from the Jyotish tradition. We keep the two layers honest: exact maths, classical interpretation, no invented accuracy numbers.",
    },
    {
      q: "Can Aura AI be wrong?",
      a: "Yes. Treat a reading as structured reflection grounded in your chart, not a verdict. For decisions about health, money or law, rely on qualified professionals; use the reading to think, not to decide alone.",
    },
  ],
  schemaName: "Aura AI Methodology",
  schemaDescription:
    "How Aura AI produces a reading: Swiss Ephemeris astronomy, classical Vedic (Jyotish) interpretation rules, and an AI language layer, with clear limits on what is claimed.",
  finalHeading: "See it on your own chart",
  finalText:
    "Generate your free kundli and ask your first question. Every answer can show the chart factors behind it.",
  disclaimer:
    "Aura AI provides astrological guidance for reflection and self-understanding. It is not medical, legal or financial advice, and it is not a substitute for consulting a qualified professional. Please use your own judgment for important decisions.",
};

// Vedic Astrologer in Dubai / UAE -----------------------------------------
//
// Written for the Gulf market, which had no page of its own, the homepage did
// not contain the words "Dubai", "UAE" or "Emirates" anywhere, so there was no
// reason for google.ae to consider the site relevant to a Dubai query.
//
// The angle is a real problem rather than a rewrite of the India copy: someone
// born in India and now living in the UAE needs their chart cast on their INDIAN
// birth coordinates and IST, not on Gulf Standard Time. Every fact quoted below
// is a fixed, checkable offset (UAE is UTC+4 with no daylight saving; India is
// UTC+5:30 with none either), which is also what answer engines lift and cite.
export const astrologerDubai: LandingConfig = {
  url: "https://astroaura.ai/vedic-astrologer-dubai",
  // Retargeted to "AI astrologer" 10 Aug 2026. See the note on globalPage:
  // the site's entire query profile is AI-astrology, and the UAE converts at
  // 18.2% CTR on the few impressions it gets, so the offer clearly lands. The
  // problem was visibility on terms the domain has no signal for, not the copy.
  title: "AI Astrologer in Dubai, Free Chat & Kundli UAE | Aura AI",
  description:
    "Chat with an AI Vedic astrologer in Dubai, free to start. Aura AI builds your kundli on your true Indian birth time and answers in seconds, 24x7 across the UAE.",
  eyebrow: "For Dubai, Abu Dhabi and the wider UAE",
  h1Pre: "Your AI Vedic",
  h1Accent: "Astrologer in Dubai",
  h1Post: ", Available Now",
  sub: "Aura AI is an AI Vedic astrologer you can consult from anywhere in the UAE, at any hour, with no appointment and no waiting room. Enter your birth details once and get your full kundli plus answers grounded in your own chart, starting free.",
  primaryCta: "Ask your first question free",
  secondaryCta: "Get your free kundli",
  areaServed: ["AE", "SA", "QA", "KW", "OM", "BH"],
  priceCurrency: "AED",
  relatedLinks: [{ to: "/indian-astrologer-usa", label: "astrologer for the USA" }],
  sections: [
    {
      h2: "Consult a Vedic astrologer in Dubai, at any hour",
      body: [
        "Aura AI answers Vedic astrology questions for residents of Dubai, Abu Dhabi, Sharjah and the rest of the UAE in seconds, at any hour, without an appointment. You enter your date, time and place of birth once, and every answer after that is read from your own kundli rather than from a generic sun-sign column.",
        "It is built for how people in the Gulf actually live. Work weeks are long, family is often in another country and a good astrologer back home may be five and a half hours ahead and asleep when you finally have a question. An AI astrologer has no consulting hours, no travel and no queue, and your first question costs nothing.",
      ],
    },
    {
      h2: "Why your birth time matters more when you live abroad",
      body: [
        "This is where most free chart tools quietly go wrong for UAE residents, and it is worth getting right. A kundli is cast on the place and moment you were born, not the place you live now. If you were born in Kerala, Kochi is your birth place forever, even after twenty years in Dubai.",
        "The two clocks involved do not line up. The UAE runs on Gulf Standard Time, UTC+4, and does not observe daylight saving. India runs on Indian Standard Time, UTC+5:30, and does not observe it either. That is a fixed ninety-minute gap. So a birth recorded as 6:00 am in Chennai is 4:30 am Dubai time, and entering 6:00 am while a tool assumes UAE time can shift your ascendant into the wrong sign, which changes every house in the chart.",
        "Aura AI resolves the timezone from the birth place you give it, so the Indian birth time on your certificate is the Indian birth time it uses.",
      ],
    },
    {
      h2: "How to get your kundli read from the UAE",
      steps: [
        "Enter your date of birth, your exact time of birth, and the city you were born in, not the city you live in now.",
        "Aura AI resolves that city's own timezone and historical offset, so an Indian birth time stays on IST and a Dubai birth stays on UTC+4.",
        "Your full Vedic kundli is generated: ascendant, twelve houses, planetary positions, nakshatras and your Vimshottari dasha timeline.",
        "Ask your question in plain English or Hindi and get an answer read from your chart, free for the first question.",
      ],
    },
    {
      h2: "What UAE residents ask Aura AI most",
      bullets: [
        "Visa, job change and relocation timing, read against the current dasha and transits.",
        "Whether to stay in the Gulf or move home, and which windows favour which choice.",
        "Marriage timing and kundli matching when the two families are in different countries.",
        "Property and long-term investment timing, including buying back in India.",
        "Business and trade-licence decisions for the self-employed.",
        "Remedies that are practical to keep up abroad, rather than rituals that need a specific temple.",
      ],
    },
    {
      h2: "How this compares to visiting an astrologer in Dubai",
      bullets: [
        "No appointment and no travel: it answers at 2 am on a Friday as readily as any other time.",
        "Private by default. You are not describing your marriage or your finances to someone in a shared waiting room.",
        "Your first question is free, and there is no consultation fee to find out whether the reading is any good.",
        "The chart maths is computed from high-grade astronomy data, so the positions are exact rather than estimated.",
        "It will not replace a trusted family astrologer for ritual guidance, and it is not meant to. It answers the questions you would otherwise sit on for weeks.",
      ],
    },
  ],
  faqs: [
    {
      q: "Is there a Vedic astrologer I can consult from Dubai right now?",
      a: "Yes. Aura AI is an AI Vedic astrologer available across the UAE 24x7, with no appointment. Enter your birth details and ask your first question free. It reads from your own kundli, not a generic sun-sign forecast.",
    },
    {
      q: "I was born in India but live in Dubai. Which birth time do I enter?",
      a: "Enter the time exactly as recorded at your Indian birth place, and enter that Indian city as your place of birth. Aura AI applies Indian Standard Time (UTC+5:30) to it automatically. Do not convert it to UAE time: the UAE is UTC+4, so converting would move your birth moment by ninety minutes and can change your ascendant.",
    },
    {
      q: "Does it work for a child born in Dubai?",
      a: "Yes. Enter Dubai as the place of birth and Aura AI applies Gulf Standard Time, UTC+4, with no daylight saving adjustment. The chart is cast on those coordinates.",
    },
    {
      q: "Is it available in Abu Dhabi and Sharjah too?",
      a: "Yes. Aura AI is available anywhere in the UAE and across the wider Gulf, including Saudi Arabia, Qatar, Kuwait, Oman and Bahrain. There is no location restriction and nothing to install.",
    },
    {
      q: "Can I ask in Hindi?",
      a: "Yes. You can ask in English or Hindi and get the answer in whichever you are more comfortable with.",
    },
    {
      q: "What does it cost in the UAE?",
      a: "Your kundli and your first question are free. Beyond that you can pay per question with wallet credits or take a subscription, and prices are shown in your local currency at checkout.",
    },
  ],
  schemaName: "Vedic Astrologer in Dubai - Aura AI",
  schemaDescription:
    "AI Vedic astrologer available across Dubai and the UAE 24x7. Free kundli cast on your true Indian birth time and timezone, with answers on career, visa and marriage timing.",
  finalHeading: "Ask your first question from Dubai, free",
  finalText:
    "No appointment, no waiting room, and a chart cast on your real birth time.",
};

// Indian Astrologer in the USA --------------------------------------------
//
// Same reasoning as the Dubai page, different market and a harder timezone
// problem: the US observes daylight saving and India does not, so the offset
// between them MOVES twice a year. That is a genuinely confusing detail for
// NRI users and exactly the sort of concrete, checkable explanation answer
// engines quote.
export const astrologerUsa: LandingConfig = {
  url: "https://astroaura.ai/indian-astrologer-usa",
  // Retargeted to "AI astrologer" 10 Aug 2026. "Indian astrologer" is kept in
  // the description and body copy for the NRI angle, but the title now leads
  // with the category the US actually finds us on (~8,800 impressions/month at
  // only 3.4% CTR against India's 7.6%, i.e. we rank but far too low).
  title: "AI Astrologer in the USA, Free Chat & Kundli | Aura AI",
  description:
    "Chat with an AI Vedic astrologer anywhere in the USA, free to start, 24x7. Built for Indians and NRIs, using your real Indian birth time, not US local time.",
  eyebrow: "For Indians and NRIs across the United States",
  h1Pre: "An AI Vedic",
  h1Accent: "Astrologer in the USA",
  h1Post: ", Without the Time Difference",
  sub: "Aura AI is an AI Vedic astrologer you can consult from any US state at any hour. No calling India at 3 am, no appointment, no waiting. Enter your birth details once and get your full kundli plus answers read from your own chart, free to start.",
  heroFacts: [
    "Awake in every US timezone",
    "IST birth times handled automatically",
    "English and Hindi",
    "First question free",
  ],
  primaryCta: "Ask your first question free",
  secondaryCta: "Get your free kundli",
  areaServed: ["US", "CA"],
  priceCurrency: "USD",
  relatedLinks: [
    { to: "/usa", label: "Vedic astrology USA hub" },
    { to: "/vedic-astrologer-dubai", label: "astrologer for Dubai and the UAE" },
    { to: "/blog/kundli-for-nris", label: "Kundli for NRIs guide" },
  ],
  sections: [
    {
      h2: "Consult an Indian astrologer from the US, on your own schedule",
      body: [
        "Aura AI answers Vedic astrology questions for people across the United States in seconds, at any hour, with no appointment. You enter your date, time and place of birth once, and every answer afterwards is read from your own kundli rather than a generic sun-sign horoscope.",
        "The reason most NRIs give up on consulting an astrologer back home is arithmetic. India is nine and a half to twelve and a half hours ahead of the US mainland depending on your state and the season, so a call that suits an astrologer in Delhi lands in the middle of your night. An AI astrologer removes the scheduling problem entirely: it is awake whenever you are.",
      ],
    },
    {
      // Target: "indian astrologer online usa" and its variants. The page
      // ranks on them but sat thin; this names the alternative honestly
      // instead of pretending the traditional route does not exist.
      h2: "Finding an Indian astrologer online in the USA, the usual way and this way",
      body: [
        "The usual way is a referral to someone in India: a WhatsApp number, a fee sent through a relative, a voice note three days later, and a follow-up question that costs another booking. It can be worth it for a practitioner your family trusts. But for the everyday questions, is this period good for a job change, does this match clear Mangal dosha, when does my Shani dasha end, the overhead outweighs the reading.",
        "Aura AI is the other way: an AI astrologer trained on classical Vedic rules that reads your actual chart and answers immediately, in English or Hindi, as many follow-ups as you need. No booking, no timezone arithmetic, no fee wired to another continent. It will not replace the family astrologer for a wedding date your grandmother must approve; it does replace the three-day wait for every ordinary question in between.",
        "It covers every US timezone by construction, Eastern through Pacific, plus Alaska and Hawaii: there is no consultation window to miss.",
      ],
    },
    {
      h2: "The birth-time mistake that breaks most NRI charts",
      body: [
        "A kundli is cast on where and when you were born, not where you live now. If you were born in Hyderabad and now live in New Jersey, Hyderabad remains your birth place permanently, and the time on your birth certificate is Indian Standard Time.",
        "The offset between the two countries is not even fixed. India runs on IST, UTC+5:30, and does not observe daylight saving. Most of the United States does. So the gap between India and US Eastern time is ten and a half hours for part of the year and nine and a half for the rest, and it differs again for Central, Mountain and Pacific. Converting your birth time by hand almost guarantees an error, and a shifted birth moment can move your ascendant into the neighbouring sign, which changes every house placement that follows.",
        "Aura AI resolves the timezone and the historical offset from the birth city you enter, so you never convert anything yourself. Type the time as printed on the certificate.",
      ],
    },
    {
      h2: "How to get your Vedic chart read from the USA",
      steps: [
        "Enter your date of birth, your exact time of birth, and the city where you were born, not your current US city.",
        "Aura AI resolves that city's timezone and the offset in force on that date, so an Indian birth stays on IST and a US birth uses the correct local time and daylight-saving state.",
        "Your full Vedic kundli is generated: ascendant, houses, planets, nakshatras and your Vimshottari dasha timeline.",
        "Ask your question in plain English or Hindi and get an answer read from your chart, free for the first question.",
      ],
    },
    {
      h2: "What people in the US ask Aura AI most",
      bullets: [
        "Green card, H-1B and citizenship timing, read against the current dasha and transits.",
        "Whether to stay in the US or return to India, and which periods favour each.",
        "Marriage timing and kundli matching when the families are on different continents.",
        "Career changes, promotions and startup timing.",
        "Buying a house in the US versus investing back in India.",
        "Remedies that are realistic away from India, without needing a particular temple or priest.",
      ],
    },
    {
      h2: "Vedic astrology and the American sun-sign horoscope are not the same",
      body: [
        "Most horoscopes published in the US are Western and tropical: they use twelve equal signs fixed to the seasons. Vedic astrology is sidereal, measured against the actual positions of the constellations, and the two drift apart by roughly twenty-four degrees. That difference is large enough that many people are a different sign in Vedic astrology than the one they grew up being told.",
        "Vedic practice also leans on tools the Western sun-sign column has no equivalent for: the nakshatras, the Vimshottari dasha system that puts timing on events, and gun milan for matching. If you have only ever read a newspaper horoscope, a kundli reading will not feel like the same thing, because it is not.",
      ],
    },
    {
      h2: "What a consultation costs from the US",
      body: [
        "Your kundli and your first question are free, with no card required to start. After that you pay per question with wallet credits, or subscribe if you consult regularly, which works out cheaper per reading. Prices are shown in your local currency at checkout, so there is no wiring money to India and no guessing at conversion.",
        "Matching, the dosha calculators and the daily panchang stay free regardless, so the running questions of a wedding season, does this date clear, does this match carry Mangal dosha, cost nothing to keep checking.",
      ],
      chips: [
        { to: "/pricing", label: "See pricing" },
        { to: "/usa", label: "All US tools in one place" },
        { to: "/kundli-matching", label: "Free kundli matching" },
      ],
    },
  ],
  faqs: [
    {
      q: "Can I consult an Indian astrologer from the USA without calling India?",
      a: "Yes. Aura AI is an AI Vedic astrologer available in every US state 24x7, so there is no time difference to work around and no appointment to book. Your first question is free.",
    },
    {
      q: "I was born in India and live in the US. Which birth time do I enter?",
      a: "Enter the time exactly as recorded on your Indian birth certificate, and enter your Indian birth city as the place of birth. Aura AI applies Indian Standard Time (UTC+5:30) automatically. Do not convert it to US time: India does not observe daylight saving and most of the US does, so the offset changes twice a year and converting by hand is how charts end up wrong.",
    },
    {
      q: "Does it handle US birth times and daylight saving?",
      a: "Yes. Enter your US city of birth and Aura AI applies that location's timezone and the daylight-saving offset that was actually in force on your birth date, rather than today's offset.",
    },
    {
      q: "Is Vedic astrology different from my usual horoscope?",
      a: "Yes. Western astrology is tropical, tied to the seasons; Vedic astrology is sidereal, tied to the observed constellations. The two differ by roughly twenty-four degrees, so your Vedic sign is often not the sun sign you know. Vedic astrology also uses nakshatras and dasha periods for timing, which Western sun-sign horoscopes do not.",
    },
    {
      q: "Can I ask in Hindi?",
      a: "Yes. Ask in English or Hindi and get your answer in the same language.",
    },
    {
      q: "What does it cost in the US?",
      a: "Your kundli and your first question are free. After that you can pay per question with wallet credits or subscribe, with prices shown in your local currency at checkout.",
    },
    {
      q: "Does it work in every US timezone?",
      a: "Yes. Eastern, Central, Mountain, Pacific, Alaska and Hawaii: the AI astrologer has no consultation hours to align with, and birth charts handle each zone's daylight-saving history automatically.",
    },
    {
      q: "Can we match kundlis if my family is in India and I am in the US?",
      a: "Yes. Kundli matching casts each chart on its own birth place and time, so the match runs identically whether both of you are in India or one of you is in New Jersey. Share the result as a starting point for the family conversation.",
    },
  ],
  schemaName: "Indian Astrologer in USA - Aura AI",
  schemaDescription:
    "AI Vedic astrologer available across the United States 24x7. Free kundli cast on your true Indian birth time and timezone, with answers on visa, career and marriage timing for NRIs.",
  finalHeading: "Ask your first question from the US, free",
  finalText:
    "No time difference, no appointment, and a chart cast on your real birth time.",
};

// USA hub -----------------------------------------------------------------
// USA batch 1 (2026-10-21). /usa previously rendered GlobalPage with a
// canonical pointing at /global, which Google has refused to index since
// July, so two sitemap URLs canonicalised into a dead end and the US front
// door contributed nothing (GSC US 28d: /usa absent from the page table
// entirely). This config makes /usa a real, self-canonical NRI hub: the
// timezone story, every free tool, panchang and muhurat in US cities, and
// the consult page, all one link deep.
export const usaHub: LandingConfig = {
  url: "https://astroaura.ai/usa",
  title: "Vedic Astrology in the USA: AI Kundli & Panchang | Aura AI",
  description:
    "Vedic astrology for Indians and NRIs in the USA: free AI kundli with the correct timezone, panchang and muhurat for US cities, kundli matching across continents. English or Hindi, 24x7.",
  eyebrow: "For the Indian diaspora across the United States",
  h1Pre: "AI Vedic",
  h1Accent: "Astrology in the USA",
  h1Post: ", in Your Timezone",
  sub: "Charts on your real birth time, festival timings after your own sunset, answers in your own hours.",
  heroFacts: [
    "IST or US birth time, resolved automatically",
    "Panchang for New York to San Francisco",
    "English and Hindi",
    "First question free",
  ],
  primaryCta: "Get my free kundli",
  secondaryCta: "Ask the AI astrologer",
  areaServed: ["US", "CA"],
  priceCurrency: "USD",
  wide: true,
  lede: [
    "Most Vedic astrology online is written for people living in India: panchang in IST, consult hours that assume you are in Delhi, kundli forms that quietly mangle a US birth time. Aura AI works the same in New Jersey as in New Delhi: every chart is cast on the exact birth time and place you enter, every timing can be read for a US city, and the AI astrologer is awake in your hours, not India's.",
  ],
  profile: [
    { label: "Timezones covered", value: "All six US zones" },
    { label: "Languages", value: "English + Hindi" },
    { label: "Free to start", value: "Kundli + first question" },
    { label: "Diwali 2026", value: "Sunday, 8 November" },
  ],
  relatedLinks: [
    { to: "/indian-astrologer-usa", label: "Indian astrologer online for the USA" },
    { to: "/vedic-astrologer-dubai", label: "astrologer for Dubai and the UAE" },
    { to: "/blog/kundli-for-nris", label: "Kundli for NRIs: birth time abroad" },
    { to: "/blog/birth-chart-us-born-desis", label: "Birth chart for US-born desis" },
    { to: "/blog/manglik-dosha-nri-marriage", label: "Manglik dosha in NRI marriages" },
    { to: "/ai-astrology", label: "AI astrology, the complete guide" },
  ],
  sections: [
    {
      h2: "The timezone problem that breaks NRI kundlis",
      body: [
        "A kundli is cast on where and when you were born, not where you live now. An Indian birth certificate time is IST, which never shifts; US clocks shift twice a year, so the offset between the two is not one number. Convert by hand and a small slip can move your lagna into the next sign, which silently changes every house in the chart.",
        "Aura AI removes the conversion entirely: type the time as printed on the certificate, pick the birth city, and the timezone and daylight-saving state in force on that date are applied for you.",
      ],
      table: {
        head: ["US timezone", "Behind IST (Mar to Nov)", "Behind IST (Nov to Mar)"],
        rows: [
          ["Eastern (New York, New Jersey, Atlanta)", "9 hrs 30 min", "10 hrs 30 min"],
          ["Central (Chicago, Dallas, Houston)", "10 hrs 30 min", "11 hrs 30 min"],
          ["Mountain (Denver, Phoenix)", "11 hrs 30 min", "12 hrs 30 min"],
          ["Pacific (San Francisco, Los Angeles, Seattle)", "12 hrs 30 min", "13 hrs 30 min"],
        ],
        caption: "Why hand-converting an IST birth time goes wrong: the offset itself changes twice a year.",
      },
    },
    {
      h2: "Every free astrology tool, one link away",
      body: [
        "The full toolkit works from any US state, free, with nothing to install. Each tool reads your actual chart, not a generic sun-sign table.",
      ],
      chips: [
        { to: "/free-kundli", label: "AI kundli free" },
        { to: "/kundli-matching", label: "Kundli matching" },
        { to: "/thirumana-porutham/", label: "Thirumana porutham" },
        { to: "/manglik-calculator", label: "Manglik calculator" },
        { to: "/mahadasha-calculator", label: "Mahadasha calculator" },
        { to: "/sade-sati-calculator", label: "Sade Sati calculator" },
        { to: "/nakshatra-calculator", label: "Nakshatra calculator" },
        { to: "/moon-sign-calculator", label: "Moon sign calculator" },
        { to: "/rising-sign-calculator", label: "Rising sign calculator" },
        { to: "/kaal-sarp-dosha", label: "Kaal Sarp dosha check" },
        { to: "/love-calculator", label: "Love calculator" },
        { to: "/daily-horoscope", label: "AI horoscope free" },
      ],
    },
    {
      h2: "Panchang, muhurat and festivals in US time",
      body: [
        "Tithis do not respect borders: a festival can begin at a different clock time, occasionally a different calendar day, in New York than in Delhi. The daily panchang here reads for US cities directly, so sunrise, tithi and the day's windows are your own, not Mumbai's.",
      ],
      bullets: [
        "Diwali 2026: Sunday, 8 November, Lakshmi Puja after your local sunset",
        "Karva Chauth 2026: late October, confirm your own city's moonrise before fasting",
        "Wedding season: vivah muhurats resume from late November 2026",
      ],
      chips: [
        { to: "/panchang", label: "Today's panchang" },
        { to: "/muhurat", label: "Shubh muhurat" },
        { to: "/blog/diwali-2026-usa", label: "Diwali 2026 in the USA" },
      ],
    },
    {
      h2: "Kundli matching when one of you is in America",
      body: [
        "Matches between a partner in the US and a partner in India are routine, and the astrology handles it cleanly: each chart is cast on its own birth place and time, then compared. Gun milan, Mangal dosha and the Navamsa work the same across continents; what goes wrong is a birth time converted to the wrong timezone first, which the tool here never asks you to do.",
      ],
      chips: [
        { to: "/kundli-matching", label: "Match two kundlis" },
        { to: "/manglik-calculator", label: "Check Mangal dosha" },
        { to: "/thirumana-porutham/", label: "Thirumana porutham" },
      ],
    },
    {
      h2: "Consulting an astrologer from the US",
      body: [
        "No WhatsApp number in India, no call at 3 am your time, no fee wired across continents.",
      ],
      steps: [
        "Enter your birth details once: date, exact time, and the city you were born in.",
        "Ask in plain English or Hindi, any hour, from any US state.",
        "Your first question is answered free, read from your own kundli.",
        "Keep going per question with wallet credits, or subscribe; prices show in your local currency at checkout.",
      ],
      chips: [
        { to: "/indian-astrologer-usa", label: "Indian astrologer online USA" },
        { to: "/talk-to-astrologer", label: "Free astrology chat" },
        { to: "/how-it-works", label: "How Aura AI works" },
      ],
    },
  ],
  faqs: [
    {
      q: "I was born in India and live in the USA. Which birth time do I use?",
      a: "Enter the time exactly as written on your Indian birth certificate and your Indian birth city as the place of birth. It stays in IST automatically. Never convert it to US time yourself: the IST to US offset changes twice a year with daylight saving, and hand conversion is the single most common cause of a wrong NRI chart.",
    },
    {
      q: "Does the panchang work for US cities?",
      a: "Yes. The daily panchang can be read for US cities such as New York, Chicago, Dallas, Houston and San Francisco, with sunrise, tithi, nakshatra and the day's windows in that city's own local time.",
    },
    {
      q: "When is Diwali 2026 in the USA?",
      a: "Diwali falls on Sunday, 8 November 2026 in the US as in India, with Lakshmi Puja performed in the Pradosh Kaal window after local sunset. The festive week runs from Dhanteras on Friday 6 November to Bhai Dooj on Wednesday 11 November.",
    },
    {
      q: "Can I match kundlis if my partner is in India and I am in the US?",
      a: "Yes. Each chart is cast on its own birth details, so the two of you being on different continents changes nothing. Enter both sets of birth details and the match runs the same gun milan and dosha checks it would in India.",
    },
    {
      q: "Is it available in Hindi?",
      a: "Yes. Ask in English or Hindi and the answer comes back in the same language.",
    },
    {
      q: "What does it cost from the US?",
      a: "Your kundli and your first question are free. After that you can pay per question with wallet credits or subscribe, with prices shown in your local currency at checkout.",
    },
  ],
  schemaName: "Vedic Astrology USA Hub - Aura AI",
  schemaDescription:
    "Vedic astrology hub for Indians and NRIs in the United States: free AI kundli with correct timezone handling, panchang and muhurat for US cities, kundli matching across continents, AI astrologer in English or Hindi.",
  finalHeading: "Start with your free kundli, from any US state",
  finalText:
    "Charts on your real birth time, timings in your own timezone, answers in your own hours.",
};

/* ---------------------------------------------------------------------------
   /ai-astrology pillar guide (USA item 9 = India item 11, one page, both
   markets). Owner of the INFORMATIONAL "ai astrology" SERP: the audit's SXO
   finding 1 showed that SERP is 100% articles/guides, so the product
   homepage can only partially match it; this guide catches it while the
   homepage keeps the commercial "ai astrology free chat" query. Carries the
   Co-Star/CHANI vs Vedic comparison for the US informational searcher. */
export const aiAstrologyPillar: LandingConfig = {
  url: "https://astroaura.ai/ai-astrology",
  title: "AI Astrology: What It Is, How Accurate, How to Try | Aura AI",
  description:
    "AI astrology, explained honestly: how an AI astrologer computes your birth chart, where it is accurate, where it is not, how Vedic AI differs from Co-Star, CHANI and ChatGPT, and how to try it free.",
  wide: true,
  eyebrow: "The complete guide",
  h1Pre: "AI",
  h1Accent: "Astrology",
  h1Post: ", Explained",
  sub: "What AI astrology actually is, how an AI astrologer reads a birth chart, the honest answer on accuracy, and how it compares with human astrologers, ChatGPT and Western apps like Co-Star. Written by the team that builds one.",
  primaryCta: "Try it: first question free",
  secondaryCta: "Make your free kundli",
  schemaName: "AI Astrology Guide - Aura AI",
  schemaDescription:
    "Complete guide to AI astrology: how AI astrologers compute birth charts, accuracy and limits, Vedic AI vs Western apps vs ChatGPT, and privacy questions answered.",
  articleSection: "Guides",
  areaServed: ["IN", "US", "AE", "GB", "CA", "AU", "SG"],
  priceCurrency: "INR",
  ogImage: "/og-ai-astrology.jpg",
  heroFacts: [
    "Charts computed with Swiss Ephemeris astronomy",
    "Vedic: lagna, nakshatra, dasha, not sun signs",
    "Honest limits, written down",
    "First question free",
  ],
  lede: [
    "AI astrology is software that computes your birth chart with real astronomical data and then interprets it using the rules of astrology, answering your questions in plain language. Done properly, the astronomy part is exact to the degree; the interpretation part is as good as the tradition and rules the system was built on, and no better.",
    "This guide explains how that works, where it is genuinely accurate, where it is not, and how a Vedic AI astrologer differs from ChatGPT, from a human astrologer, and from Western apps like Co-Star and CHANI. We build one of these systems, so we will also tell you exactly what it cannot do.",
  ],
  sections: [
    {
      h2: "What is AI astrology?",
      body: [
        "AI astrology combines two very different components. The first is astronomical computation: taking your date, time and place of birth and calculating exactly where every planet stood, which is pure mathematics with a single correct answer. Serious systems use the Swiss Ephemeris, built on NASA JPL data, for this step. The second is interpretation: applying astrological rules to that chart and writing the answer in language you can act on. This is where systems differ, because the quality depends on which tradition's rules were encoded and how carefully.",
        "The practical difference from a horoscope website is personalisation. A daily horoscope gives one paragraph to a twelfth of humanity. An AI astrologer starts from your own chart, so two people asking the same question get different answers for stated reasons.",
      ],
    },
    {
      h2: "How an AI astrologer reads your chart",
      steps: [
        "Birth details in: date, time and place. Time matters most, it fixes the ascendant (lagna) and the twelve houses.",
        "Chart computed: planetary longitudes from the ephemeris, nakshatras to the pada, divisional charts, and the Vimshottari dasha timeline counted from the Moon's nakshatra.",
        "Rules applied: house lordships, yogas with their cancellation conditions, dasha and current transits, the same checklist a classical Jyotish reading walks through.",
        "Answer written: the verdict plus the reasoning, naming which placement produced it, so you can judge the logic instead of taking it on faith.",
      ],
    },
    {
      h2: "How accurate is AI astrology, honestly",
      body: [
        "Split the question in two. The chart itself: effectively exact. Planetary positions are astronomy, and any serious system computes them to the degree. If a tool gets your Moon sign wrong, it is not doing this step properly. The interpretation: as accurate as astrology itself, which is a system of patterns refined over centuries, not a laboratory science. An honest AI astrologer does not quote accuracy percentages, does not predict events like a camera watching the street, and tells you when a question belongs to a doctor or a lawyer instead.",
        "What AI does add is consistency and breadth: it checks more chart factors at once than a person can hold in mind, applies the same rules every time, and never has a bad day. What it cannot add is lived human judgment of your situation. Our full method, including what we deliberately do not claim, is public on the methodology page.",
      ],
      chips: [{ to: "/methodology", label: "Our methodology, including the limits" }],
    },
    {
      h2: "AI astrologer vs ChatGPT",
      body: [
        "A general chatbot can talk about astrology, but it does not compute your chart: it predicts plausible text, and when we tested it with real birth details it placed the Moon a sign away from the ephemeris value, then built a confident reading on the wrong chart. A purpose-built AI astrologer calculates first and interprets second, which is the entire difference. We published both experiments with the chat transcripts.",
      ],
      chips: [
        { to: "/blog/ai-astrology-vs-chatgpt", label: "The ChatGPT kundli test, in full" },
        { to: "/blog/chatgpt-se-kundli", label: "ChatGPT se kundli: Hinglish mein" },
      ],
    },
    {
      h2: "Vedic AI vs Western astrology apps",
      body: [
        "If you have used Co-Star or CHANI, you have met Western tropical astrology: sun-sign forward, psychological in tone, built around daily push notifications. Vedic astrology is a different system, not a different skin: it uses the sidereal zodiac (your sign can differ), reads from the Moon sign and nakshatra, and its timing engine is the dasha sequence, which has no Western equivalent. Neither is a subset of the other; they answer differently shaped questions.",
      ],
      table: {
        head: ["", "Western apps (Co-Star, CHANI)", "Vedic AI (Aura AI)"],
        rows: [
          ["Zodiac", "Tropical, sun-sign led", "Sidereal, Moon sign and lagna led"],
          ["Core detail", "Psychological daily readings", "Nakshatra, houses, yogas, doshas"],
          ["Timing", "Transits", "Vimshottari dasha plus transits"],
          ["Marriage questions", "Compatibility by element", "36-guna kundli matching, manglik check"],
          ["Format", "Daily push notifications", "Ask anything chat on your chart"],
        ],
        caption: "The two systems compared, feature by feature",
      },
    },
    {
      h2: "AI astrologer vs a human astrologer",
      body: [
        "A good human astrologer brings intuition, context and accountability; an AI brings breadth, consistency, availability and privacy. The honest summary from our longer comparison: AI wins on pattern coverage and never misreads the ephemeris; humans win on nuance and empathy. Many people use both, the AI for everyday questions and timing checks, a trusted astrologer for life's biggest calls.",
      ],
      chips: [
        { to: "/blog/ai-astrology-vs-human-astrology", label: "The full AI vs human comparison" },
        { to: "/talk-to-astrologer", label: "Talk to an AI astrologer" },
      ],
    },
    {
      h2: "What people actually ask an AI astrologer first",
      body: [
        "The first questions are strikingly consistent across countries. Career: should I change jobs this year, when does my situation improve, is a business yoga present in my chart. Marriage: when, with whom by nakshatra compatibility, what the manglik check really says after cancellations. Money: the difference between an earning year and a saving year in the dasha. Health and family questions follow, usually asked at hours when no human consultation is open. The pattern underneath them is the same: not tell me my future, but help me time a decision I am already weighing.",
        "That is also the honest frame for using one. Bring a real question, give exact birth details, and read the reasoning the answer cites. A chart-based reply that names its houses and dasha can be checked, discussed with family, even taken to a human astrologer for a second opinion. A vague reassurance cannot, whoever wrote it.",
      ],
    },
    {
      h2: "Privacy, and the questions people actually ask",
      body: [
        "Birth details are sensitive data, and the questions people bring to astrology are often the ones they would not say out loud. A system you can trust states plainly how data is handled: conversations encrypted, birth data stored in anonymised form, nothing sold. It is also why many people prefer asking software: no judgment, no audience, no queue at 3 AM.",
      ],
    },
    {
      h2: "How to try AI astrology free",
      body: [
        "The fastest honest test: generate your free kundli, check the Moon sign and lagna against any panchang source you trust, then ask one real question and read whether the answer names its reasoning from your chart. That two-minute test separates computed astrology from decorated guessing.",
      ],
      chips: [
        { to: "/free-kundli", label: "Free kundli" },
        { to: "/ai-astrologer", label: "AI astrologer, 24x7" },
        { to: "/kundli-matching", label: "Kundli matching" },
        { to: "/free-astrology", label: "All free astrology tools" },
      ],
    },
  ],
  faqs: [
    {
      q: "What is AI astrology?",
      a: "AI astrology is software that computes your birth chart from your date, time and place of birth using astronomical data, then interprets the chart with astrological rules and answers your questions in plain language. The computation is exact; the interpretation follows the tradition the system was built on, Vedic Jyotish in Aura AI's case.",
    },
    {
      q: "Is AI astrology accurate?",
      a: "The chart is computed with Swiss Ephemeris astronomy, so the planetary positions are exact. The interpretation is as reliable as the astrological tradition behind it, applied consistently. No honest system quotes accuracy percentages or guarantees events; if one does, treat that as a warning sign.",
    },
    {
      q: "Can ChatGPT do astrology?",
      a: "ChatGPT can discuss astrology but does not reliably compute charts: in our published test it placed the Moon in the wrong sign and built the reading on that error. A dedicated AI astrologer calculates the chart first, then interprets, which is the fundamental difference.",
    },
    {
      q: "What is the difference between Vedic and Western AI astrology apps?",
      a: "Western apps like Co-Star and CHANI use the tropical zodiac, read sun-sign first and focus on psychological daily guidance. Vedic AI uses the sidereal zodiac, reads the Moon sign, lagna and nakshatra, and times events with the Vimshottari dasha. Your sign itself can differ between the two systems.",
    },
    {
      q: "Is AI astrology free to use?",
      a: "On Aura AI the kundli, kundli matching, the calculators, panchang and daily horoscope are free, and your first chat question is free with no card. After that, chats are paid from wallet credits, and regular users can take an optional subscription.",
    },
    {
      q: "Does an AI astrologer work for people born outside India?",
      a: "Yes, as long as the birth time and place are entered correctly: the chart is computed for the exact coordinates and time zone of the birth place, including historical daylight saving rules. That is also what matters for NRIs born in the US or elsewhere.",
    },
  ],
  finalHeading: "Read enough? Test it on your own chart",
  finalText:
    "Theory settles nothing in astrology. Generate your free kundli, ask one real question, and judge the reasoning yourself. The first question costs nothing.",
  relatedLinks: [
    { to: "/", label: "AI astrology free chat" },
    { to: "/indian-astrologer-usa", label: "Indian astrologer for the USA" },
    { to: "/blog/kundli-for-nris", label: "Kundli for NRIs born abroad" },
  ],
};

export const LANDING_PAGES: LandingConfig[] = [
  freeKundli,
  kundliMatching,
  dailyHoroscope,
  talkToAstrologer,
  astrologerDubai,
  astrologerUsa,
  usaHub,
  aiAstrologyPillar,
];

/* ---------------------------------------------------------------------------
   Love Calculator. First of the free-calculator pages.

   Head terms: "love calculator", "love calculator by name", "love percentage
   calculator", "love meter", "true love calculator". The tool itself is pure
   client-side numerology (src/utils/loveScore.ts), so this page carries no API
   dependency and cannot be rate limited.

   The copy deliberately does NOT claim the score is predictive. It is a
   numerology tool, said plainly, with the real chart reading positioned as the
   next step. A page that overclaims is the one that gets picked apart.
--------------------------------------------------------------------------- */
export const loveCalculator: LandingConfig = {
  url: "https://astroaura.ai/love-calculator",
  title: "Love Calculator by Name, Free Love Percentage Test | Aura AI",
  description:
    "Free love calculator by name. Put in two names, get your love percentage straight away, and see the numerology behind the score. Then ask Aura AI what your charts say.",
  eyebrow: "Free Love Calculator",
  h1Pre: "Love Calculator by Name,",
  h1Accent: "Free",
  sub: "Two names, one number, about two seconds. No sign-up. And we show you where the score came from instead of just handing you a figure.",
  primaryCta: "Ask Aura AI about your match",
  secondaryCta: "Get your free kundli",
  schemaName: "Love Calculator by Name",
  schemaDescription:
    "Free name-based love percentage calculator using Pythagorean numerology, with an explanation of the number behind every score.",
  sections: [
    {
      h2: "What is a love calculator?",
      body: [
        "It turns two names into one number. You will see it called a love meter, a love percentage calculator, a love match calculator, a true love calculator. Same thing, different label.",
        "It is not a prediction, and we are not going to pretend otherwise. It is numerology: every letter gets a number, the numbers get added up, and the totals get compared. That is genuinely interesting. It also knows nothing about your history, or what either of you is like after a bad week.",
        "So treat it as a starting point. Run the names, see the number, then go looking for a proper answer if you want one.",
      ],
    },
    {
      h2: "How the love calculator by name works",
      body: [
        "We use Pythagorean numerology, the most common system. Each letter is worth one to nine. A is 1, B is 2, on to I at 9, then it starts over at J.",
        "Add up each name, reduce it to a single number, and you have two roots. Those get compared. Numerology treats some pairings as easy and others as hard work. A 2 and a 6 are both relationship numbers, so they sit together comfortably. A 4 and a 5 are steady versus restless, so they take more effort.",
        "Plenty of names share a root, though, so we run a second pass over the raw letter totals to separate them. That is why Priya and Preeti do not score identically against the same partner.",
        "One thing worth checking for yourself: the score never changes. Same two names, same number, every time, and it does not matter who you type first. If a calculator gives you something different on every reload, it is not calculating anything.",
      ],
      steps: [
        "Put your name in the first box",
        "Put the other name in the second, partner, crush, friend, whoever",
        "Hit Calculate love percentage",
        "Read the score, the band it lands in, and the two name numbers underneath",
      ],
    },
    {
      h2: "What your love percentage actually means",
      body: [
        "Look at the band, not the exact figure. A 79 and an 81 are the same result. The gap is noise.",
      ],
      bullets: [
        "85 to 99. Strong. Talking tends to come easily and you are comfortable quickly. Still takes work, it just starts further ahead.",
        "70 to 84. Good, with room in it. You fit together without turning into the same person. The gaps are usually the interesting bit.",
        "55 to 69. Different temperaments. Not a warning. A lot of long relationships live right here, held together by people who actually talk to each other.",
        "38 to 54. More contrast than overlap. Says nothing at all about loyalty, kindness or whether you want the same things, because a name cannot see any of that.",
      ],
    },
    {
      h2: "Names or birth dates, which is better?",
      body: [
        "Names are quick and you always have them. If a name is all you have got, this is your tool.",
        "Birth details tell you much more. A date, a time and a place give you a full Vedic chart, and a chart holds things a name never will. Where Venus sits, which governs affection. The seventh house, which governs partnership. The Moon, which governs how you feel things. And the dasha period you are in, which is often the real reason a relationship felt easy last year and hard this one.",
        "That is the honest difference. A name gives you a number. A chart gives you a reason. If you want the reason, make your free kundli and ask Aura AI, or run a proper kundli matching if marriage is the question.",
      ],
    },
    {
      h2: "Love compatibility by zodiac element",
      body: [
        "If you know both signs, the elements are a second way in. Every sign sits in one of four, and the element says a lot about how someone shows up in a relationship.",
      ],
      bullets: [
        "Fire, so Aries, Leo and Sagittarius. Direct, full of energy, quick to commit and quick to get bored. Easiest with Fire and Air.",
        "Earth, so Taurus, Virgo and Capricorn. Steady, practical, slow to let you in and solid once they have. Easiest with Earth and Water.",
        "Air, so Gemini, Libra and Aquarius. Talkers, curious, drawn to anyone who can keep up. Easiest with Air and Fire.",
        "Water, so Cancer, Scorpio and Pisces. Feel everything, protective, pick up on the mood in a room. Easiest with Water and Earth.",
      ],
    },
    {
      h2: "Who actually uses this",
      body: [
        "Nearly everyone, at some point, for completely different reasons. Someone with a new crush wanting a push either way. A couple three years in who run it for a laugh and then argue about the result. Friends doing their own names. Somebody thinking about marriage having a quiet first look before the serious version.",
        "All fine. Just keep the weight of the answer in line with the weight of the question you asked it. Two names is not much to go on.",
      ],
    },
    {
      h2: "Beyond the percentage",
      body: [
        "A number cannot tell you why a relationship feels the way it does. That needs a chart.",
        "Aura AI reads your actual birth chart and answers in normal language. Ask why the same argument keeps coming back. Ask whether this is a sensible time to commit. Ask what the two charts say together. It names the placements behind each answer, so you can see the reasoning instead of being handed a verdict.",
        "First question is free, and you do not need to know any astrology to ask it.",
      ],
    },
  ],
  faqs: [
    {
      q: "How is the love percentage calculated by name?",
      a: "Every letter gets a number from one to nine, using Pythagorean numerology. Both names are added up and reduced to a root, and those two roots are compared on a compatibility grid. A second pass over the full letter totals separates names that happen to share a root, which is what gives each pair its own score.",
    },
    {
      q: "Is the love calculator accurate or scientifically proven?",
      a: "No. It is numerology, not science, and there is no research showing names predict relationships. It is a good starting point and we would rather say that than dress it up. If you want an answer with reasoning behind it, use a birth chart instead of a name.",
    },
    {
      q: "Why do different love calculators give different results?",
      a: "Different methods. Some use Pythagorean numerology, some use Chaldean, some fold in birth dates or zodiac signs, and some just return a random number. Ours is Pythagorean, and it is deterministic, so the same two names always come back the same.",
    },
    {
      q: "Does the order of the names change the score?",
      a: "No. It is symmetric. Type your name first or second, you get the same percentage.",
    },
    {
      q: "What does a low love percentage mean?",
      a: "That the two names share fewer numerological threads. That is all. Trust, respect, timing and wanting the same things decide far more, and this tool cannot see a single one of them.",
    },
    {
      q: "Can a love calculator predict marriage?",
      a: "No. For marriage, Vedic astrology uses kundli matching, which compares both charts across the ashtakoot gunas and looks for problems like Mangal Dosha. That needs birth details, not names.",
    },
  ],
  finalHeading: "Ask about the chart, not just the number",
  finalText:
    "You have got the percentage. If you want to know what is actually driving it, Aura AI reads both charts and explains it in plain language. First question free.",
  relatedLinks: [
    { to: "/kundli-matching", label: "Kundli Matching" },
    { to: "/free-kundli", label: "Free Kundli" },
    { to: "/marriage/", label: "AI Marriage Prediction" },
    { to: "/ai-astrologer", label: "AI Astrologer" },
    { to: "/blog/love-calculator-what-the-score-means", label: "What your love score means" },
  ],
};

/* ---------------------------------------------------------------------------
   The four chart-backed calculators. Each is one call to the `calculators`
   edge function, rendered by VedicCalculator. Copy is written per page rather
   than templated, because four pages with the nouns swapped is the pattern
   Google's scaled-content policy exists to catch.
--------------------------------------------------------------------------- */

export const manglikCalculator: LandingConfig = {
  disclaimer:
    "Mangal Dosha is a concept from traditional Vedic astrology, not a medical, psychological or legal finding. This page is for guidance and self reflection only. A Manglik result says nothing about your compatibility, your health or your worth as a partner, and it should never be the sole basis for a decision about marriage. For decisions that affect your wellbeing or your family, please consult a qualified professional.",
  url: "https://astroaura.ai/manglik-calculator",
  title: "Manglik Calculator, Free Mangal Dosha Check | Aura AI",
  description:
    "Free Manglik calculator. Enter your birth details and find out if you have Mangal Dosha, checked from the Lagna, the Moon and Venus, with what it actually means.",
  eyebrow: "Free Manglik Calculator",
  h1Pre: "Manglik Calculator,",
  h1Accent: "Free",
  sub: "Find out whether Mars sits in a Manglik house in your chart. We check from all three reference points and tell you which ones apply.",
  primaryCta: "Ask Aura AI about your chart",
  secondaryCta: "Get your free kundli",
  schemaName: "Manglik Calculator",
  schemaDescription:
    "Free Mangal Dosha calculator using your Vedic birth chart, checked from the Lagna, the Moon and Venus.",
  sections: [
    {
      h2: "What is Mangal Dosha?",
      body: [
        "Mangal Dosha, or being Manglik, means Mars sits in one of a handful of houses in your birth chart: the first, second, fourth, seventh, eighth or twelfth. Those houses touch temperament, family, marriage and conflict, and Mars is the planet of drive and friction. Put the two together and tradition says it needs handling carefully in marriage.",
        "It is one of the most searched things in Indian astrology and one of the most misunderstood. It is not a curse and it is not rare. Depending on which reference point you use, a large share of charts qualify.",
      ],
      chips: [
        { to: "/blog/manglik-dosha-bhang", label: "Manglik dosha bhang ke niyam" },
        { to: "/nadi-dosh", label: "Nadi dosh" },
      ],
    },
    {
      h2: "Why we check from three places",
      body: [
        "Most free calculators check Mars from the Lagna, your rising sign, and stop there. That is only part of the tradition.",
        "Mars can also be counted from the Moon, which is what many North Indian astrologers use, and from Venus, which is the significator of marriage itself. A chart can be Manglik from one of those and clean from the others.",
        "That is why our result shows all three rows separately instead of collapsing them into one yes or no. If you have ever had two astrologers give you different answers, this is usually why.",
      ],
    },
    {
      h2: "What to do if you are Manglik",
      body: [
        "First, do not panic, and be sceptical of anyone who tells you to. The strength and placement of Mars matters, as does whether the other chart carries the same thing, since two Manglik charts are traditionally considered to cancel.",
        "The honest position is that this is one factor in a full kundli matching, not a verdict on its own. If marriage is the real question, run a proper match rather than a single dosha check.",
      ],
    },
    {
      h2: "Why your birth time matters here",
      body: [
        "The Lagna changes roughly every two hours, so an hour of error can move your rising sign and shift which house Mars occupies. That can flip the result.",
        "If you are unsure of your time, take the answer as indicative and ask Aura AI, which can read the chart and tell you how close to a boundary you are.",
      ],
    },
    {
      h2: "The six Manglik houses, and what each one is said to touch",
      body: [
        "Mars in a different house does not mean the same thing. Tradition treats each placement separately, and any astrologer worth the fee will ask which house yours is in before saying anything else.",
      ],
      bullets: [
        "First house, the Lagna. Mars sits on the self. Read as a strong temper and a strong will, and the reason the person may struggle to back down in an argument.",
        "Second house. The house of family, speech and accumulated wealth. The traditional concern is friction inside the household and words said in heat.",
        "Fourth house. Home, mother, peace of mind. Mars here is read as restlessness at home and difficulty settling in one place.",
        "Seventh house. The house of marriage itself, which is why this placement gets the most attention. Read as intensity in partnership rather than absence of it.",
        "Eighth house. Longevity, inheritance, sudden change. The most cautioned placement in classical texts, and the one most often overstated online.",
        "Twelfth house. Loss, expenditure, the bed. Traditionally linked to distance between partners, physical or otherwise.",
      ],
    },
    {
      h2: "Manglik Dosha cancellation, or Bhang",
      body: [
        "This is the part most free calculators skip, and it is the part that decides how much any of it matters. Classical astrology lists a long set of conditions under which the dosha is considered cancelled or substantially reduced.",
      ],
      bullets: [
        "Both charts are Manglik. The most widely accepted cancellation, and the reason matchmakers pair one Manglik with another.",
        "Mars in its own sign, Aries or Scorpio, or exalted in Capricorn. A dignified Mars behaves very differently from a weak one.",
        "Mars aspected or conjunct Jupiter, the natural benefic. Jupiter is traditionally said to soften what Mars sharpens.",
        "Mars in a sign belonging to Saturn, Capricorn or Aquarius, which is read as Mars being disciplined rather than let loose.",
        "Certain age thresholds. Several traditions hold that the effect reduces substantially after 28, which corresponds roughly to the first Saturn return.",
        "A strong seventh house or a strong seventh lord elsewhere in the chart, which can carry the marriage regardless.",
      ],
    },
    {
      h2: "What the tradition suggests, and what we will not tell you",
      body: [
        "The standard remedies you will read about include Mangal worship on Tuesdays, the Hanuman Chalisa, feeding or donating on a Tuesday, and in some traditions Kumbh Vivah before marriage. Red coral is the stone associated with Mars, and it is the one most often sold on the back of a Manglik result.",
        "We are not going to tell you that you must buy anything. A gemstone is prescribed on the strength and dignity of a planet in a specific chart, not on a yes or no from a free tool, and anyone selling you one off a single flag is selling, not reading.",
        "If you want to know whether a remedy is appropriate for your chart, ask. That is a chart question, and it deserves a chart answer.",
      ],
    },
    {
      h2: "Manglik and kundli matching",
      body: [
        "In a full kundli match, Mangal Dosha is checked alongside the ashtakoot gunas, the 36 point compatibility score built from eight factors covering temperament, health, mentality, affection, longevity and progeny.",
        "A common misreading is to treat the dosha as a veto that overrides the guna score. It is not. It is one input, and matchmakers weigh it against the rest of both charts. Plenty of matches with a Manglik chart score well overall, and plenty of non Manglik matches score badly.",
        "If marriage is the actual question you came here with, run the full match rather than acting on a single flag.",
      ],
    },
    {
      h2: "Three things people get wrong about being Manglik",
      body: [
        "First, that it is rare. It is not. Depending on which reference point you count from, a substantial share of charts qualify, which is exactly why the tradition developed so many cancellations.",
        "Second, that it predicts a specific event. It does not. It describes a tendency in how Mars expresses itself around partnership, and tendencies are shaped by everything else in the chart and by the person.",
        "Third, that a website can settle it. A calculator can tell you where Mars sits. Whether that matters for you, in your chart, at your age, with this person, is a reading.",
      ],
    },
  ],
  faqs: [
    {
      q: "What makes someone Manglik?",
      a: "Mars sitting in the first, second, fourth, seventh, eighth or twelfth house of the birth chart. We check that from the Lagna, the Moon and Venus, and show you which of the three apply.",
    },
    {
      q: "Is being Manglik bad?",
      a: "No. It is a factor to account for, not a defect. Its weight depends on the strength and sign of Mars and on the other person's chart, and traditionally two Manglik charts cancel each other.",
    },
    {
      q: "Why do different calculators give different Manglik results?",
      a: "Because most check only from the Lagna. Counting from the Moon or from Venus can give a different answer, and both are part of the tradition. We show all three rather than picking one.",
    },
    {
      q: "Can Mangal Dosha be cancelled?",
      a: "Vedic astrology recognises several cancellations, including Mars in its own or exalted sign, certain aspects, and both charts carrying the dosha. Those depend on the whole chart, which is a conversation rather than a checkbox.",
    },
  ],
  finalHeading: "One factor, not a verdict",
  finalText:
    "Mangal Dosha is a single line in a much longer chart. Ask Aura AI what the rest of yours says, and it will tell you how much weight this actually deserves. First question free.",
  relatedLinks: [
    { to: "/kundli-matching", label: "Kundli Matching" },
    { to: "/free-kundli", label: "Free Kundli" },
    { to: "/kaal-sarp-dosha", label: "Kaal Sarp Dosha" },
    { to: "/blog/manglik-dosha-explained", label: "What is Manglik Dosha? Read the full explanation" },
  ],
};

export const kaalSarpCalculator: LandingConfig = {
  disclaimer:
    "Kaal Sarp Dosha is a concept from traditional Vedic astrology, not a medical, psychological or financial diagnosis. This page is for guidance and self reflection only. It does not predict misfortune, and no remedy described here is guaranteed to change any outcome. If you are worried about your health, your money or your safety, please speak to a qualified professional.",
  url: "https://astroaura.ai/kaal-sarp-dosha",
  title: "Kaal Sarp Dosha Calculator, Free Check | Aura AI",
  description:
    "Free Kaal Sarp Dosha calculator. Enter your birth details to see whether every planet in your chart falls on one side of the Rahu to Ketu axis, and what that means.",
  eyebrow: "Free Kaal Sarp Check",
  h1Pre: "Kaal Sarp Dosha",
  h1Accent: "Calculator",
  sub: "One question, answered from your actual chart: do all your planets sit between Rahu and Ketu?",
  primaryCta: "Ask Aura AI about your chart",
  secondaryCta: "Get your free kundli",
  schemaName: "Kaal Sarp Dosha Calculator",
  schemaDescription:
    "Free Kaal Sarp Dosha calculator that checks whether every planet in your Vedic birth chart falls on one side of the Rahu to Ketu axis.",
  sections: [
    {
      h2: "What Kaal Sarp Dosha actually is",
      body: [
        "Rahu and Ketu always sit exactly opposite each other, so they cut your chart into two halves. Kaal Sarp Dosha forms when every one of the seven planets falls on one side of that line, with nothing on the other.",
        "The name is dramatic and the internet has made it more so. In practice it is a pattern, and like any pattern its effect depends on which houses the axis runs through and how strong the rest of the chart is.",
      ],
    },
    {
      h2: "It is a yes or no, and that is the point",
      body: [
        "Unlike most things in a chart, this one is binary. Either a planet sits outside the axis or none does. There is no partial Kaal Sarp, whatever a paid report might suggest.",
        "That makes it a good thing to check yourself and a bad thing to be sold. If a site charges you to find out, it is charging you for one comparison.",
      ],
    },
    {
      h2: "If your chart has it",
      body: [
        "The useful question is not whether you have it but where the axis falls. Rahu in the tenth with Ketu in the fourth reads very differently from Rahu in the sixth with Ketu in the twelfth, and the houses involved are what an astrologer would actually discuss with you.",
        "Ask Aura AI and it will name the houses in your chart rather than giving you the generic version.",
      ],
    },
    {
      h2: "The twelve types, named by where the axis falls",
      body: [
        "Kaal Sarp is not one thing. Classical texts name twelve variants depending on which house Rahu occupies, and each is read differently. The name is far less useful than the houses, but here is the map people search for.",
      ],
      bullets: [
        "Anant, Rahu in the first house and Ketu in the seventh. Read against self and partnership.",
        "Kulik, Rahu in the second, Ketu in the eighth. Family, speech and accumulated wealth.",
        "Vasuki, Rahu in the third, Ketu in the ninth. Courage, siblings, and belief.",
        "Shankhpal, Rahu in the fourth, Ketu in the tenth. Home and career pulling against each other.",
        "Padma, Rahu in the fifth, Ketu in the eleventh. Education, children and gains.",
        "Mahapadma, Rahu in the sixth, Ketu in the twelfth. Conflict, health and expenditure.",
        "Takshak, Rahu in the seventh, Ketu in the first. Partnership read from the other side.",
        "Karkotak, Rahu in the eighth, Ketu in the second. Sudden change and inheritance.",
        "Shankhachur, Rahu in the ninth, Ketu in the third. Fortune, father and belief.",
        "Ghatak, Rahu in the tenth, Ketu in the fourth. Career pressing on home life.",
        "Vishdhar, Rahu in the eleventh, Ketu in the fifth. Networks, gains and creativity.",
        "Sheshnag, Rahu in the twelfth, Ketu in the sixth. Isolation, expense and hidden matters.",
      ],
    },
    {
      h2: "Ascending and descending, and why it changes the reading",
      body: [
        "There is a second distinction that matters as much as the type. If the planets sit in the half running from Rahu towards Ketu in zodiacal order, it is called ascending, or Udit. If they run from Ketu towards Rahu, it is descending, Anudit.",
        "The traditional reading is that ascending tends to concentrate difficulty in the first half of life and ease it later, while descending does the reverse. Whether you find that convincing or not, it explains why two people with the same named type report very different experiences of it.",
      ],
    },
    {
      h2: "What Rahu and Ketu actually are",
      body: [
        "They are not planets. They are the two points where the Moon's orbit crosses the ecliptic, the path the Sun appears to travel. Eclipses happen when a new or full Moon lands near one of them, which is precisely why the tradition treats them as points of sudden change.",
        "Because they are geometric opposites, they are always exactly 180 degrees apart. That is the whole reason Kaal Sarp is even possible as a pattern: two fixed points cutting the chart into two halves, and the question of whether anything landed on the empty side.",
        "It also means Rahu and Ketu move in reverse through the zodiac and take about eighteen months per sign, so the axis in your chart is shared by everyone born within roughly a year and a half of you. What makes it yours is which houses it falls in, which depends on your birth time.",
      ],
    },
    {
      h2: "Remedies, and a word of caution",
      body: [
        "The commonly cited remedies are Rahu and Ketu shanti at Trimbakeshwar or Ujjain, Nag Panchami observance, Maha Mrityunjaya recitation, and donations of items associated with the two nodes. Many people find real value in ritual, and we are not going to be dismissive about that.",
        "What we will say plainly is that Kaal Sarp is one of the most commercially exploited terms in Indian astrology. If a site tells you your life is blocked until you pay for a specific puja, that is a business model, not a reading. The pattern is in your chart either way, and so is everything that modifies it.",
      ],
    },
    {
      h2: "So how much should you actually worry about it?",
      body: [
        "Less than the internet suggests. Kaal Sarp describes a concentration: all your planetary energy pushed into one half of the chart. Concentration can read as intensity and single mindedness as easily as it reads as obstruction.",
        "What decides the outcome is the same thing that decides everything else in a chart. Which houses. Which signs. Whether the planets involved are strong or weak. Which dasha period you are currently running. Those questions have answers, and none of them fit in a yes or no.",
      ],
    },
  ],
  faqs: [
    {
      q: "How is Kaal Sarp Dosha calculated?",
      a: "By checking whether all seven planets fall between Rahu and Ketu, which always sit exactly opposite one another. If even one planet is outside that arc, the dosha does not form.",
    },
    {
      q: "Is Kaal Sarp Dosha serious?",
      a: "It is one pattern among many. Its weight depends on which houses the Rahu to Ketu axis runs through and on the strength of the rest of the chart. Treating it as a standalone catastrophe is a sales tactic, not astrology.",
    },
    {
      q: "Can Kaal Sarp Dosha go away?",
      a: "The chart you are born with does not change. What changes is the dasha period you are running, which is usually why the same chart feels heavy in one decade and fine in another.",
    },
  ],
  finalHeading: "The axis matters more than the label",
  finalText:
    "Knowing you have it tells you very little. Knowing which houses Rahu and Ketu occupy tells you a lot. Ask Aura AI and it will read yours. First question free.",
  relatedLinks: [
    { to: "/manglik-calculator", label: "Manglik Calculator" },
    { to: "/sade-sati-calculator", label: "Sade Sati Calculator" },
    { to: "/free-kundli", label: "Free Kundli" },
  ],
};

export const sadeSatiCalculator: LandingConfig = {
  disclaimer:
    "Sade Sati is a concept from traditional Vedic astrology, not a medical, psychological or financial diagnosis. This page is for guidance and self reflection only. Being in Sade Sati does not mean something bad will happen to you. If you are going through a hard period and it is affecting your health or your mind, please reach out to a qualified professional or someone you trust.",
  url: "https://astroaura.ai/sade-sati-calculator",
  title: "Sade Sati Calculator, Are You In It Right Now | Aura AI",
  description:
    "Free Shani Sade Sati calculator. Enter your birth details to see whether Saturn is currently transiting your Moon sign, and which phase you are in.",
  eyebrow: "Free Sade Sati Check",
  h1Pre: "Sade Sati Calculator,",
  h1Accent: "Right Now",
  sub: "Saturn takes seven and a half years to cross your Moon sign and its neighbours. This tells you whether you are in that stretch today.",
  primaryCta: "Ask Aura AI about your chart",
  secondaryCta: "Get your free kundli",
  schemaName: "Sade Sati Calculator",
  schemaDescription:
    "Free Shani Sade Sati calculator that checks whether Saturn is currently transiting the sign before your Moon, your Moon sign, or the one after.",
  sections: [
    {
      h2: "What Sade Sati is",
      body: [
        "Saturn spends about two and a half years in each sign. Sade Sati is the stretch when it passes through the sign before your Moon sign, then your Moon sign, then the one after. Three signs, roughly seven and a half years.",
        "It gets talked about as a bad period. A fairer description is a demanding one. Saturn's reputation is for slowing things down, removing what is not load bearing and asking you to do the work properly.",
      ],
      chips: [
        { to: "/blog/sade-sati-2026-27", label: "Sade sati 2026-27 timeline" },
        { to: "/shani-mahadasha", label: "Shani mahadasha" },
      ],
    },
    {
      h2: "This answer changes with time, not with you",
      body: [
        "Everything else on this site is worked out from your birth chart, which never changes. This one is different. It compares Saturn's position today against your Moon sign, so the same birth details give a different answer next year.",
        "That is also why it is worth rechecking rather than remembering. If you ran it two years ago, the answer may well have flipped.",
      ],
    },
    {
      h2: "The three phases are not the same",
      body: [
        "The first phase, Saturn in the sign before your Moon, tends to show up around obligations and the ground shifting. The second, Saturn on your Moon, is the one people describe as heavy, because the Moon governs the mind. The third eases as Saturn moves off.",
        "Where you are in the seven and a half years matters more than whether you are in it at all. Ask Aura AI and it will tell you which phase and how long is left.",
      ],
    },
    {
      h2: "The three phases in detail",
      body: [
        "Sade Sati is usually described as a single seven and a half year block. In practice it is three distinct two and a half year passes, and people experience them very differently.",
      ],
      bullets: [
        "Rising phase, Saturn in the twelfth from your Moon. Traditionally the phase of expenditure, sleep disruption, foreign matters and things quietly draining away. Often the phase people only recognise in hindsight.",
        "Peak phase, Saturn on your Moon sign. The Moon governs the mind, so this is the stretch associated with heaviness, self doubt and the sense of carrying more than usual. It is also the phase most often credited afterwards with forcing a genuine change of direction.",
        "Setting phase, Saturn in the second from your Moon. Read as pressure around money, family and speech, easing as Saturn moves away. Recovery usually starts here rather than at the very end.",
      ],
    },
    {
      h2: "Dhaiya, the small Sade Sati",
      body: [
        "There is a shorter version people often confuse with the main event. Kantaka Shani, commonly called Dhaiya, is Saturn transiting the fourth or the eighth house from your Moon sign. It lasts about two and a half years, one sign, rather than seven and a half.",
        "The fourth house version is read against home and peace of mind, the eighth against sudden change and health. If someone has told you that you are in Sade Sati but the timing does not fit, this is very often what they actually meant.",
      ],
    },
    {
      h2: "Why Saturn moves so slowly, and why that matters",
      body: [
        "Saturn takes about twenty nine and a half years to travel the full zodiac, so roughly two and a half years per sign. That single fact explains the whole structure: three signs at two and a half years each gives the seven and a half years.",
        "It also means Sade Sati is not a rare misfortune that singles you out. It comes round two or three times in an average life, once in youth, once in middle age, sometimes once more later, and everyone born under your Moon sign is going through it at the same time as you.",
        "The first Saturn return, when Saturn comes back to where it sat at your birth, lands around age twenty nine and is often confused with Sade Sati. They are different transits, though they can overlap.",
      ],
    },
    {
      h2: "What actually helps",
      body: [
        "The traditional remedies are Saturday observance, Shani worship, the Hanuman Chalisa, donating black sesame, iron, or mustard oil, and feeding those who have less. Blue sapphire is the stone linked to Saturn, and it is also the stone most often mis-sold, because a badly matched sapphire is traditionally held to do real harm.",
        "The practical reading of Saturn is more useful than the shopping list. Saturn rewards preparation, consistency and honest accounting, and it exposes anything built on shortcuts. People who come out of Sade Sati well are usually the ones who used it to fix foundations rather than to wait it out.",
        "If you want to know which of your houses Saturn is currently sitting on and what that specifically touches, that is a chart question. Ask it.",
      ],
    },
    {
      h2: "Sade Sati by moon sign, roughly",
      body: [
        "Because Saturn moves through the signs in order, Sade Sati arrives in a predictable sequence. Whichever sign Saturn currently occupies, the three Moon signs in Sade Sati are that sign, the one before it and the one after.",
        "So if Saturn is in Pisces, the Moon signs affected are Aquarius, Pisces and Aries. When Saturn moves into Aries, Aquarius is released and Taurus begins. That is the whole mechanism, and it is why the calculator above can answer it from your Moon sign and today's date alone.",
      ],
    },
  ],
  faqs: [
    {
      q: "How do I know if I am in Sade Sati?",
      a: "Compare Saturn's current sign against your Moon sign. If Saturn is in the sign before your Moon, in your Moon sign, or in the one after, you are in Sade Sati. This calculator does that comparison against today.",
    },
    {
      q: "How long does Sade Sati last?",
      a: "About seven and a half years, made up of three phases of roughly two and a half years each as Saturn crosses the three signs.",
    },
    {
      q: "Is Sade Sati always bad?",
      a: "No. It is usually demanding rather than disastrous, and what it demands depends on where Saturn sits in your chart and how strong it is. Plenty of people do their most solid work during it.",
    },
    {
      q: "Does Sade Sati come more than once?",
      a: "Yes. Saturn takes about twenty nine and a half years to go round the zodiac, so most people meet Sade Sati two or three times in a life.",
    },
  ],
  finalHeading: "Which phase, and how long left",
  finalText:
    "Knowing you are in it is the small half of the answer. Ask Aura AI which phase you are in, what it is touching in your chart, and when Saturn moves on. First question free.",
  relatedLinks: [
    { to: "/moon-sign-calculator", label: "Moon Sign Calculator" },
    { to: "/mahadasha-calculator", label: "Mahadasha Calculator" },
    { to: "/free-kundli", label: "Free Kundli" },
    { to: "/kumbh-rashifal", label: "Kumbh rashifal during sade sati" },
  ],
};

export const moonSignCalculator: LandingConfig = {
  url: "https://astroaura.ai/moon-sign-calculator",
  title: "Moon Sign Calculator, Find Your Rashi Free | Aura AI",
  description:
    "Free moon sign calculator. Enter your birth details to find your Vedic rashi, the degree, the house and your nakshatra, and see why it differs from your sun sign.",
  eyebrow: "Free Moon Sign Calculator",
  h1Pre: "Moon Sign Calculator,",
  h1Accent: "Your Rashi",
  sub: "Your rashi is where the Moon sat when you were born. It is the sign Indian astrology actually works from, and it is usually not the one you think.",
  primaryCta: "Ask Aura AI about your chart",
  secondaryCta: "Get your free kundli",
  schemaName: "Moon Sign Calculator",
  schemaDescription:
    "Free Vedic moon sign calculator returning your sidereal rashi, degree, house and nakshatra from your birth details.",
  sections: [
    {
      h2: "Moon sign, not sun sign",
      body: [
        "The sign you know from magazines is your sun sign, worked out from the tropical zodiac used in Western astrology. Your rashi is your Moon sign, worked out from the sidereal zodiac used in Vedic astrology.",
        "The two systems have drifted about 24 degrees apart, so most people get a different answer from each. If this page tells you something other than the sign you have called yourself for years, nothing has gone wrong. You are looking at a different measurement.",
        "Vedic astrology leans on the Moon rather than the Sun because the Moon governs the mind, which is where daily prediction actually lives. Your rashifal is written for this sign, not your sun sign.",
      ],
    },
    {
      h2: "What you get back",
      body: [
        "The rashi itself, the exact degree the Moon had reached inside it, the house it occupies in your chart, and your nakshatra with its ruling planet.",
        "The degree is worth a look. A Moon at 29 degrees is about to change sign, so a small error in your birth time can move it. If you are near a boundary, take the result as approximate.",
      ],
    },
    {
      h2: "Why the nakshatra matters",
      body: [
        "The 12 signs are the coarse view. The 27 nakshatras divide the same circle far more finely, which is why Vedic astrology uses them for matching, timing and naming.",
        "Two people can share a rashi and have completely different nakshatras, and in practice the nakshatra often describes someone better than the sign does.",
      ],
    },
    {
      h2: "Why the two zodiacs disagree",
      body: [
        "Both systems divide the sky into twelve. They disagree about where to start counting.",
        "Western astrology uses the tropical zodiac, which begins at the spring equinox. Vedic astrology uses the sidereal zodiac, which begins from a fixed star position. Because the Earth wobbles slowly on its axis, a motion called precession, those two starting points have drifted apart at roughly one degree every seventy two years.",
        "The gap is now about twenty four degrees, a little under one sign. That is the entire reason your rashi is usually the sign before your Western sun sign. Neither system is broken. They are measuring from different origins, and the difference is called ayanamsa.",
      ],
    },
    {
      h2: "What your Moon sign is actually used for",
      body: [
        "In Vedic astrology the Moon is the anchor for far more than personality. Daily, weekly and yearly rashifal is written against the Moon sign. Sade Sati is measured from it. The Vimshottari dasha sequence, which sets the timing of everything, starts from the nakshatra the Moon occupied at birth.",
        "That is why an Indian astrologer will ask for your rashi rather than your sun sign, and why reading a horoscope written for the wrong sign gives you nothing useful.",
        "The Moon also governs the mind and the emotional temperament, which is the practical reason it carries more weight in prediction. The Sun describes the self you present. The Moon describes how you actually feel about it.",
      ],
    },
    {
      h2: "The twelve rashis in one line each",
      body: [
        "Your Moon sign colours how you process things rather than how you appear. Read these as emotional temperament, not personality summary.",
      ],
      bullets: [
        "Mesha, Aries. Reacts fast, recovers fast, hates being managed.",
        "Vrishabha, Taurus. Slow to unsettle and slow to move, needs security before anything else.",
        "Mithuna, Gemini. Processes by talking, gets restless in silence.",
        "Karka, Cancer. Feels everything first, remembers how things felt long after the details are gone.",
        "Simha, Leo. Needs to be seen, takes being overlooked harder than being criticised.",
        "Kanya, Virgo. Copes by fixing, struggles to sit with a problem that cannot be solved.",
        "Tula, Libra. Reads the room constantly, uncomfortable until things are balanced.",
        "Vrishchika, Scorpio. Goes deep, holds on, rarely shows the half of it.",
        "Dhanu, Sagittarius. Needs room and a reason, does badly when boxed in.",
        "Makara, Capricorn. Carries weight quietly, mistakes endurance for wellbeing.",
        "Kumbha, Aquarius. Steps back to think, can look detached while caring a lot.",
        "Meena, Pisces. Absorbs the mood around them, needs to be able to retreat.",
      ],
    },
    {
      h2: "The 27 nakshatras, and why they are the finer instrument",
      body: [
        "Twelve signs divide the circle into thirty degree blocks. Twenty seven nakshatras divide the same circle into blocks of thirteen degrees and twenty minutes, and each of those is divided again into four padas.",
        "That is more than four times the resolution. Two people born under the same rashi can sit in different nakshatras with different ruling planets, and in practice the nakshatra often describes someone more accurately than the sign does.",
        "Nakshatras also do the real work in traditional practice. Kundli matching scores are built from them, the dasha sequence starts from them, and in many families the first letter of a child's name is chosen from the pada of their birth star.",
      ],
    },
    {
      h2: "Moon sign, sun sign, rising sign",
      body: [
        "Three different questions, three different answers, and mixing them up is the most common reason a reading feels wrong.",
        "Your Moon sign is where the Moon sat at birth, and it governs the mind. Your sun sign is where the Sun sat, and it governs the core self. Your rising sign, the Lagna, is the sign that was on the eastern horizon at the exact minute you were born, and it sets the entire house structure of the chart.",
        "The rising sign is the one most sensitive to birth time, changing roughly every two hours. The Moon changes sign every two and a quarter days, so it is far more forgiving if your recorded time is approximate.",
      ],
    },
  ],
  faqs: [
    {
      q: "What is the difference between moon sign and sun sign?",
      a: "The moon sign is where the Moon was at your birth, measured on the sidereal zodiac used in Vedic astrology. The sun sign is where the Sun was, measured on the tropical zodiac used in Western astrology. The two zodiacs have drifted about 24 degrees apart, so the answers usually differ.",
    },
    {
      q: "Which sign should I read my horoscope for?",
      a: "For Vedic astrology and rashifal, your moon sign. That is the sign the predictions are written against.",
    },
    {
      q: "Do I need my exact birth time for my moon sign?",
      a: "The Moon moves about one degree every two hours, so it changes sign roughly every two and a quarter days. A rough time is usually enough, unless the Moon was near the end of a sign, which the degree in your result will show.",
    },
    {
      q: "What is a nakshatra?",
      a: "One of 27 lunar mansions dividing the zodiac more finely than the 12 signs. Yours is set by the Moon's exact position, and it is used for compatibility, timing and traditional naming.",
    },
  ],
  finalHeading: "Now ask what it means",
  finalText:
    "You have the rashi, the degree and the nakshatra. Aura AI can tell you what that Moon placement actually does in your chart, in plain language. First question free.",
  relatedLinks: [
    { to: "/sade-sati-calculator", label: "Sade Sati Calculator" },
    { to: "/daily-horoscope", label: "Daily Horoscope" },
    { to: "/free-kundli", label: "Free Kundli" },
  ],
};

export const nakshatraCalculator: LandingConfig = {
  url: "https://astroaura.ai/nakshatra-calculator",
  title: "Nakshatra Calculator, Find Your Birth Star Free | Aura AI",
  description:
    "Free nakshatra calculator. Enter your birth details to find your birth star, its pada, its ruling planet and what the 27 nakshatras actually mean.",
  eyebrow: "Free Nakshatra Calculator",
  h1Pre: "Nakshatra Calculator,",
  h1Accent: "Your Birth Star",
  sub: "The 27 nakshatras divide the zodiac far more finely than the 12 signs. Find yours, its pada, and the planet that rules it.",
  primaryCta: "Ask Aura AI about your chart",
  secondaryCta: "Get your free kundli",
  schemaName: "Nakshatra Calculator",
  schemaDescription:
    "Free Vedic nakshatra calculator returning your birth star, pada and ruling planet from your birth details.",
  sections: [
    {
      h2: "What a nakshatra is",
      body: [
        "The zodiac is a circle of 360 degrees. The twelve signs cut it into blocks of thirty. The twenty seven nakshatras cut the same circle into blocks of thirteen degrees and twenty minutes, and each of those is divided again into four padas of three degrees twenty.",
        "Your nakshatra is whichever of those the Moon occupied at the moment you were born. Because the Moon moves about thirteen degrees a day, it crosses roughly one nakshatra per day, which makes this far more specific to you than a sun sign shared with a twelfth of the planet.",
        "The system predates the twelve sign zodiac in Indian use. The nakshatras are named after the star groups that mark them, and much of classical Jyotish is built on them rather than on the signs.",
      ],
      chips: [
        { to: "/blog/janam-nakshatra-kaise-jaane", label: "Janam nakshatra kaise jaane" },
        { to: "/ashwini-nakshatra", label: "Ashwini Nakshatra" },
        { to: "/bharani-nakshatra", label: "Bharani Nakshatra" },
        { to: "/krittika-nakshatra", label: "Krittika Nakshatra" },
        { to: "/rohini-nakshatra", label: "Rohini Nakshatra" },
        { to: "/mrigashira-nakshatra", label: "Mrigashira Nakshatra" },
        { to: "/ardra-nakshatra", label: "Ardra Nakshatra" },
        { to: "/punarvasu-nakshatra", label: "Punarvasu Nakshatra" },
        { to: "/pushya-nakshatra", label: "Pushya Nakshatra" },
        { to: "/ashlesha-nakshatra", label: "Ashlesha Nakshatra" },
        { to: "/magha-nakshatra", label: "Magha Nakshatra" },
        { to: "/purva-phalguni-nakshatra", label: "Purva Phalguni Nakshatra" },
        { to: "/uttara-phalguni-nakshatra", label: "Uttara Phalguni Nakshatra" },
        { to: "/hasta-nakshatra", label: "Hasta Nakshatra" },
        { to: "/chitra-nakshatra", label: "Chitra Nakshatra" },
        { to: "/swati-nakshatra", label: "Swati Nakshatra" },
        { to: "/vishakha-nakshatra", label: "Vishakha Nakshatra" },
        { to: "/anuradha-nakshatra", label: "Anuradha Nakshatra" },
        { to: "/jyeshtha-nakshatra", label: "Jyeshtha Nakshatra" },
        { to: "/mula-nakshatra", label: "Mula Nakshatra" },
        { to: "/purva-ashadha-nakshatra", label: "Purva Ashadha Nakshatra" },
        { to: "/uttara-ashadha-nakshatra", label: "Uttara Ashadha Nakshatra" },
        { to: "/shravana-nakshatra", label: "Shravana Nakshatra" },
        { to: "/dhanishta-nakshatra", label: "Dhanishta Nakshatra" },
        { to: "/shatabhisha-nakshatra", label: "Shatabhisha Nakshatra" },
        { to: "/purva-bhadrapada-nakshatra", label: "Purva Bhadrapada Nakshatra" },
        { to: "/uttara-bhadrapada-nakshatra", label: "Uttara Bhadrapada Nakshatra" },
        { to: "/revati-nakshatra", label: "Revati Nakshatra" },
      ],
    },
    {
      h2: "Why the pada matters",
      body: [
        "Each nakshatra splits into four padas, and the pada places you inside a navamsa, the ninth division of the chart used to judge the strength of a placement and, traditionally, marriage.",
        "It is also the practical reason people ask for their nakshatra at all. In many Indian families the first syllable of a child's name is chosen from the pada of their birth star, which is why the same nakshatra produces recognisable clusters of names.",
      ],
    },
    {
      h2: "Your nakshatra sets your dasha",
      body: [
        "Vimshottari dasha, the timing system that decides when things in a chart are supposed to happen, does not start from your sign. It starts from the exact position of the Moon inside your nakshatra.",
        "Each nakshatra has a ruling planet, and that planet's period is the one you were born into. How far through the nakshatra the Moon had travelled decides how much of that first period was left. This is why two people born on the same day, hours apart, can be running completely different dashas for decades.",
      ],
    },
    {
      h2: "Nakshatras in matching",
      body: [
        "Ashtakoot gun milan, the 36 point marriage matching system, is built almost entirely from nakshatras. Tara, Yoni, Gana and Nadi all come from the birth star rather than the sign, which is four of the eight kootas and twenty one of the thirty six points.",
        "Nadi in particular carries eight points on its own, and it is decided purely by which of three groups the two nakshatras fall into. If you have ever heard that a match failed on Nadi dosha, this is what was being checked.",
      ],
    },
  ],
  faqs: [
    {
      q: "How do I find my nakshatra?",
      a: "From your date, time and place of birth. The nakshatra is whichever of the 27 divisions the Moon occupied at that moment, so it is calculated from the Moon's exact longitude rather than looked up from a date.",
    },
    {
      q: "Do I need an exact birth time for my nakshatra?",
      a: "Less than for a rising sign. The Moon takes about a day to cross a nakshatra, so an hour of error rarely changes it, though it can shift the pada if the Moon was near a boundary.",
    },
    {
      q: "What is a nakshatra pada?",
      a: "A quarter of a nakshatra, three degrees and twenty minutes wide. It fixes your navamsa position and is traditionally used to choose the first syllable of a name.",
    },
    {
      q: "Is nakshatra more important than the zodiac sign?",
      a: "For classical Vedic practice, often yes. Dasha timing, marriage matching and much traditional prediction work from the nakshatra rather than the sign.",
    },
  ],
  finalHeading: "Your star, and what it is doing",
  finalText:
    "You have the nakshatra, the pada and the ruling planet. Ask Aura AI what that placement means in your chart and which dasha it started you in. First question free.",
  relatedLinks: [
    { to: "/moon-sign-calculator", label: "Moon Sign Calculator" },
    { to: "/mahadasha-calculator", label: "Mahadasha Calculator" },
    { to: "/kundli-matching", label: "Kundli Matching" },
  ],
};

export const risingSignCalculator: LandingConfig = {
  url: "https://astroaura.ai/rising-sign-calculator",
  title: "Rising Sign Calculator, Find Your Lagna Free | Aura AI",
  description:
    "Free rising sign calculator. Enter your birth details to find your Vedic Lagna, the ascendant degree and its nakshatra, and why birth time matters most here.",
  eyebrow: "Free Rising Sign Calculator",
  h1Pre: "Rising Sign Calculator,",
  h1Accent: "Your Lagna",
  sub: "The sign climbing over the horizon at your birth. It sets every house in your chart, and it is the one placement that genuinely needs an accurate time.",
  primaryCta: "Ask Aura AI about your chart",
  secondaryCta: "Get your free kundli",
  schemaName: "Rising Sign Calculator",
  schemaDescription:
    "Free Vedic rising sign and ascendant calculator returning your Lagna, its degree and its nakshatra.",
  sections: [
    {
      h2: "What the rising sign is",
      body: [
        "As the Earth turns, the zodiac appears to rotate past the eastern horizon. Whichever sign was rising there at the exact minute of your birth is your Lagna, your ascendant, your rising sign.",
        "The whole zodiac passes the horizon once a day, so the rising sign changes roughly every two hours. That makes it the fastest moving thing in your chart and the one most sensitive to birth time.",
      ],
    },
    {
      h2: "Why it decides the shape of the chart",
      body: [
        "Your Lagna becomes the first house. Every other house is counted from it. Move the Lagna by one sign and every planet in your chart lands in a different house, which changes what each one is read to govern.",
        "That is why an astrologer will press you on the birth time and why a chart drawn from a guessed time is worth very little. Mars in the seventh and Mars in the eighth are different readings, and two hours can be the whole difference.",
        "It is also why the same person can be told they are Manglik by one astrologer and not by another, if the two are working from different birth times.",
      ],
    },
    {
      h2: "Rising sign, sun sign, moon sign",
      body: [
        "Three placements, three jobs. The rising sign builds the house structure and describes how you meet the world. The Sun describes the core self. The Moon governs the mind and is what Vedic prediction actually runs on.",
        "Western astrology tends to lead with the Sun. Vedic astrology leads with the Moon and the Lagna, and treats the Sun as one planet among nine.",
      ],
    },
    {
      h2: "If you do not know your birth time",
      body: [
        "You are not stuck, but be honest about the uncertainty. A rough time still gives a usable Moon sign and nakshatra, since those move far more slowly.",
        "Astrologers use birth time rectification to narrow an unknown time by working backwards from events that have already happened. It is skilled work and it is not something a calculator can do. If your time is genuinely unknown, ask Aura AI and it will tell you which parts of your reading are safe and which are not.",
      ],
    },
  ],
  faqs: [
    {
      q: "What is the difference between rising sign and sun sign?",
      a: "The rising sign is the sign on the eastern horizon at your birth, which changes every two hours. The sun sign is where the Sun was, which changes once a month. They answer different questions and usually differ.",
    },
    {
      q: "How accurate does my birth time need to be?",
      a: "For the rising sign, as accurate as you can get it. The Lagna changes roughly every two hours, so being an hour out can move it into the neighbouring sign and shift every house in the chart.",
    },
    {
      q: "Is the Vedic rising sign the same as the Western one?",
      a: "Usually not. Vedic astrology uses the sidereal zodiac and Western uses the tropical, and the two have drifted about 24 degrees apart, so the Lagna often falls in the previous sign.",
    },
    {
      q: "Why is the rising sign called the Lagna?",
      a: "Lagna is the Sanskrit term for the ascending point. Charts are commonly named after it, so a Taurus Lagna chart is one where Taurus was rising at birth.",
    },
  ],
  finalHeading: "The chart is built from here",
  finalText:
    "Your Lagna decides where every planet lands. Ask Aura AI what yours puts in each house, and what that means in practice. First question free.",
  relatedLinks: [
    { to: "/free-kundli", label: "Free Kundli" },
    { to: "/moon-sign-calculator", label: "Moon Sign Calculator" },
    { to: "/nakshatra-calculator", label: "Nakshatra Calculator" },
  ],
};

// OPTIMISE 2026-10-13 (item 19): no cannibalisation here, the page is 1-of-1
// for "mahadasha calculator" (5 clicks / 473 imp @10.4, 1.1% CTR), but GSC 28d
// showed three gaps. The free modifier earned nothing ("mahadasha calculator
// free" 25 imp @7.9, 0 clicks). Current-dasha intent ranked ~3 with 0% CTR
// ("current mahadasha" 22 imp @3.1, "mahadasha" 22 @3.1, "current dasha" 7
// @3.7). Planet-name searches sat at ~9-10 with almost no clicks (shani 57
// imp, ketu 55, rahu 48, dasha 35, saturn 22, venus 10). Title/H1/sub now
// carry free + current, a planet-by-planet section answers the shani/rahu/ketu
// searches, and a FAQ answers "which mahadasha is going on".
export const dashaCalculator: LandingConfig = {
  url: "https://astroaura.ai/mahadasha-calculator",
  title: "Mahadasha Calculator Free: Check Your Current Dasha | Aura AI",
  description:
    "Free mahadasha calculator: see your current mahadasha and antardasha, when each period ends, and what a Shani, Ketu or Rahu dasha means in your own chart.",
  eyebrow: "Free Mahadasha Calculator",
  h1Pre: "Mahadasha Calculator,",
  h1Accent: "Your Current Dasha",
  sub: "Vedic astrology's timing system, free. See your current mahadasha and antardasha, when each one ends, and the sequence for the rest of your life.",
  primaryCta: "Ask Aura AI about your chart",
  secondaryCta: "Get your free kundli",
  schemaName: "Mahadasha Calculator Free",
  schemaDescription:
    "Free mahadasha calculator showing your current mahadasha and antardasha, their end dates, and your full Vimshottari timeline from your birth details.",
  sections: [
    {
      h2: "What Vimshottari dasha is",
      body: [
        "A birth chart shows what is in play. Dasha shows when. Vimshottari is the main timing system in Vedic astrology, dividing a notional 120 year lifespan between the nine planets in a fixed order and with fixed lengths.",
        "Ketu gets 7 years, Venus 20, the Sun 6, the Moon 10, Mars 7, Rahu 18, Jupiter 16, Saturn 19 and Mercury 17. That sequence never changes. What changes between people is where in the cycle you start.",
      ],
      chips: [
        { to: "/blog/mahadasha-kaise-dekhe", label: "Mahadasha kaise dekhe" },
        { to: "/shani-mahadasha", label: "Shani mahadasha" },
        { to: "/rahu-mahadasha", label: "Rahu mahadasha" },
        { to: "/guru-mahadasha", label: "Guru mahadasha" },
      ],
    },
    {
      h2: "Where your sequence begins",
      body: [
        "It begins from the nakshatra your Moon occupied at birth. Each of the 27 nakshatras is ruled by one of the nine planets, and that planet's period is the one you were born into.",
        "How far the Moon had travelled through that nakshatra decides how much of the first period was already spent. Someone born at the very start of an Ashwini Moon gets almost the full seven years of Ketu; someone born at the end gets months.",
        "That is why two people born on the same morning can be running different periods for the next thirty years, and it is the strongest practical argument for knowing your birth time.",
      ],
    },
    {
      h2: "The layers inside a mahadasha",
      body: [
        "A mahadasha is the outer period, and it can run for nearly two decades. Nothing meaningful would be predictable at that resolution, so it subdivides.",
        "Inside each mahadasha are antardashas, sub-periods in the same nine planet order. Inside those are pratyantardashas, and it keeps going. The tool above shows your current mahadasha and pratyantardasha, which is usually the useful pair.",
        "In practice an astrologer reads the combination. Jupiter mahadasha with a Saturn antardasha behaves very differently from Jupiter with a Venus antardasha, even though the outer period is identical.",
      ],
    },
    {
      h2: "Shani, Rahu, Ketu: what each mahadasha tends to bring",
      body: [
        "People usually search for their period by planet name, often with some dread attached: Shani mahadasha, Rahu mahadasha, Ketu mahadasha. Each does have a theme. Saturn (Shani) runs nineteen years of slow, structural work. Rahu's eighteen are ambitious, worldly and restless. Ketu's seven detach you from things you thought you needed.",
        "Venus brings twenty years coloured by relationships, comfort and creative work. Jupiter's sixteen lean toward growth, teachers and faith, Mercury's seventeen toward commerce, learning and communication. The Sun's six are about visibility and authority, the Moon's ten about home and emotional life, Mars's seven about energy and initiative.",
        "Treat these as the period's questions, not its verdict. Whether a Shani mahadasha builds or grinds depends on Saturn's condition in your chart, which is exactly what the calculator together with your kundli can show you.",
      ],
    },
    {
      h2: "How to read a period you are worried about",
      body: [
        "A dasha is not good or bad by planet name. Saturn's period is not automatically hard and Venus's is not automatically easy. What matters is how that planet sits in your chart: which house it rules, which house it occupies, whether it is strong, and what it aspects.",
        "A well placed Saturn running its own period can be one of the most productive stretches of a life. A badly placed Venus can be one of the most unsettled. The planet is the clock; your chart is the content.",
        "If a period ahead is worrying you, that is a chart question rather than a calendar question. Ask it.",
      ],
    },
  ],
  faqs: [
    {
      q: "What is a mahadasha?",
      a: "The main planetary period in the Vimshottari system. Each of the nine planets rules one, with fixed lengths from six years for the Sun to twenty for Venus, and they run in a fixed order.",
    },
    {
      q: "How is my dasha calculated?",
      a: "From the exact position of the Moon at birth. The nakshatra the Moon occupied sets which planet's period you start in, and how far through that nakshatra the Moon had travelled sets how much of it was left.",
    },
    {
      q: "Is a Saturn mahadasha bad?",
      a: "Not by itself. Saturn's period reflects how Saturn sits in your chart. A strong, well placed Saturn running its own period is often a long stretch of solid, durable progress.",
    },
    {
      q: "What is the difference between mahadasha and antardasha?",
      a: "The mahadasha is the outer period, which can last up to twenty years. Antardashas are the sub-periods within it, cycling through the same nine planets in the same order.",
    },
    {
      q: "How do I know which mahadasha is going on for me?",
      a: "Enter your date, time and place of birth above. The calculator reads the Moon's nakshatra at your birth, runs the Vimshottari sequence forward to today, and shows your current mahadasha and antardasha with their end dates.",
    },
  ],
  finalHeading: "Which period, and what it touches",
  finalText:
    "You have the timeline. Ask Aura AI how the ruling planet sits in your chart, because that is what decides how the period actually plays out. First question free.",
  relatedLinks: [
    { to: "/nakshatra-calculator", label: "Nakshatra Calculator" },
    { to: "/sade-sati-calculator", label: "Sade Sati Calculator" },
    { to: "/free-kundli", label: "Free Kundli" },
  ],
};

// Thirumana Porutham (South Indian matching) -------------------------------
// A SEPARATE SYSTEM, not a synonym for kundli matching, which is why it gets
// its own page rather than another Ashtakoot variant: Ashtakoot is the North
// Indian method (8 kootas, 36 gunas), porutham is the Tamil one (10 poruthams,
// read from nakshatra). Different method, different audience, different words.
// Google Trends India, 12 months: porutham averages 76 against kundli
// matching's 42, and we rank for none of it.
export const thirumanaPorutham: LandingConfig = {
  url: "https://astroaura.ai/thirumana-porutham/",
  title: "Thirumana Porutham: Free 10 Porutham Marriage Matching Online",
  description:
    "Free thirumana porutham check for marriage. All 10 poruthams explained, from Dinam and Ganam to Rajju and Vedha, with nakshatra and rasi matching.",
  eyebrow: "",
  h1Pre: "Free",
  h1Accent: "Thirumana Porutham",
  h1Post: " for Marriage",
  sub: "Check marriage compatibility the South Indian way. Porutham is matched from the birth nakshatra and rasi across ten factors, from Dinam and Ganam through to Rajju and Vedha.",
  primaryCta: "Check porutham free",
  secondaryCta: "Ask about compatibility",
  articleSection: "Vedic astrology",
  ogImage: "/og-thirumana-porutham.png",
  wide: true,
  hideHeroCtas: true,
  relatedLinks: [
    { to: "/kundli-matching", label: "kundli matching" },
    { to: "/free-kundli", label: "free kundli" },
  ],
  sections: [
    {
      h2: "What is thirumana porutham?",
      body: [
        "Thirumana porutham is the Tamil system of marriage matching. Thirumanam means marriage and porutham means compatibility or agreement, so the phrase is literally marriage compatibility. It is used across Tamil Nadu, and closely related systems are used in Kerala and parts of Karnataka and Andhra Pradesh.",
        "The match is read from each person's birth nakshatra (the star the Moon occupied at birth) and their rasi (Moon sign). Ten factors are checked in turn, and each is either agreeable, partially agreeable or not. There is no single score out of a fixed total the way there is in the North Indian system: a family looks at how many poruthams agree, and at which ones.",
        "Most traditions treat the match as acceptable when the majority of the ten agree, and pay particular attention to a small group considered essential. Which ones count as essential varies by family and region, which is one of the genuine differences between this and Ashtakoot.",
      ],
    },
    {
      h2: "Porutham and Ashtakoot are not the same system",
      body: [
        "This is the single most common confusion, and it matters because the two methods can disagree about the same couple.",
        "Ashtakoot, the North Indian method, scores eight kootas out of 36 gunas and produces one number. Porutham checks ten factors and produces a list of agreements rather than a total. Several factors overlap in substance (Yoni, Ganam, Rasi and Rasi Athipathi all have Ashtakoot counterparts), but the weighting is different and porutham gives Rajju, which is read for the longevity of the marriage, a prominence that Ashtakoot has no direct equivalent for.",
        "Neither is a broken version of the other. They are two traditions with different priorities, and it is normal for a family to follow the one native to their region.",
      ],
      chips: [
        { to: "/kundli-matching", label: "North Indian: kundli matching" },
        { to: "/free-kundli", label: "Build a free kundli" },
        { to: "/manglik-calculator", label: "Check Manglik status" },
      ],
    },
    {
      h2: "The 10 poruthams",
      body: [
        "Each porutham is read between the two birth nakshatras, or between the two rasis, and judged agreeable or not.",
      ],
      table: {
        head: ["Porutham", "Read from", "What it covers"],
        rows: [
          ["Dinam", "Nakshatra", "Health, wellbeing and prosperity of the couple"],
          ["Ganam", "Nakshatra", "Temperament: Deva, Manushya or Rakshasa nature"],
          ["Mahendram", "Nakshatra", "Progeny, and the continuation of the family line"],
          ["Stree Deergham", "Nakshatra", "The wellbeing and longevity of the bride"],
          ["Yoni", "Nakshatra", "Physical and intimate compatibility"],
          ["Rasi", "Moon sign", "Family growth and standing over the long term"],
          ["Rasi Athipathi", "Moon sign lord", "Mental and emotional rapport between the two"],
          ["Vasya", "Moon sign", "Mutual attraction and the balance of influence"],
          ["Rajju", "Nakshatra", "Longevity of the marriage; treated as the most serious"],
          ["Vedha", "Nakshatra", "Affliction between the two stars; checked for obstacles"],
        ],
        caption:
          "The ten poruthams of Tamil thirumana porutham, and what each is read for.",
      },
    },
    {
      h2: "Which poruthams matter most?",
      body: [
        "Families rarely require all ten. The usual approach is to look at how many agree and then at which ones, because they are not equal in weight.",
      ],
      cards: [
        {
          label: "Rajju",
          title: "Treated as essential",
          body: "Read for the longevity of the marriage, and the one most traditions are least willing to set aside. A Rajju mismatch is taken seriously even when the rest agree.",
          tone: "bad",
        },
        {
          label: "Dinam, Ganam, Yoni",
          title: "Strongly weighted",
          body: "Health, temperament and intimate compatibility. These are the day-to-day registers of a marriage, and a mismatch here is felt constantly rather than occasionally.",
          tone: "ok",
        },
        {
          label: "Rasi, Rasi Athipathi",
          title: "Long-term harmony",
          body: "Family standing and mental rapport. Both have direct Ashtakoot counterparts (Bhakoot and Graha Maitri), so a North Indian reading will usually agree with a South Indian one here.",
          tone: "good",
        },
        {
          label: "Mahendram, Stree Deergham, Vasya, Vedha",
          title: "Supporting",
          body: "Progeny, the bride's wellbeing, mutual attraction and affliction between the stars. Weighed after the heavier factors, and often where a family allows some give.",
          tone: "best",
        },
      ],
    },
    {
      h2: "How porutham is checked, step by step",
      steps: [
        "Take the date, exact time and place of birth for both people. Time and place matter because they fix the Moon's position, and the Moon is what porutham is read from.",
        "Compute each person's birth nakshatra, its pada, and the rasi the Moon occupied.",
        "Count the nakshatra-based poruthams in turn: Dinam, Ganam, Mahendram, Stree Deergham, Yoni, Rajju and Vedha.",
        "Check the rasi-based ones: Rasi, Rasi Athipathi and Vasya.",
        "Read the result as a group rather than a total: how many agree, and whether any of the heavy ones (Rajju in particular) do not.",
        "Check the wider chart alongside it, including Mangal dosha and the current dasha periods, which porutham does not cover.",
      ],
    },
    {
      h2: "Rajju porutham, and why it is treated so seriously",
      subs: [
        {
          h3: "What Rajju measures",
          body: [
            "Rajju divides the 27 nakshatras into five groups, each associated with a part of the body: Paadha (foot), Ooru (thigh), Nabhi or Kadi (waist), Kanda (neck) and Siro (head). When both partners fall in the same Rajju group, the porutham does not agree.",
            "It is read for the longevity of the marriage, which is why most traditions treat it as the one to check first and the hardest to set aside.",
          ],
        },
        {
          h3: "Is a Rajju mismatch final?",
          body: [
            "Not universally. Some traditions accept the match when the two fall in the same group but on opposite sides of it, and some weigh a strong showing across the other nine against it. Practice varies by family and by region more than any other porutham, which is worth knowing before treating one result as a verdict.",
          ],
        },
      ],
    },
    {
      h2: "Porutham by nakshatra and rasi, not by name",
      body: [
        "Porutham is computed from the birth nakshatra and the rasi, both of which come from the date, exact time and place of birth. That is the accurate method.",
        "Matching by name is a fallback for when birth details are unknown: the first syllable of a name is used to infer a likely nakshatra. It is a real traditional convention, but it approximates a single input rather than measuring it. If you have both birth dates, use them.",
        "If a birth time is genuinely unknown, enter a best estimate rather than skipping the check. The nakshatra and rasi usually hold steady across a few hours, so most poruthams stay stable even when the ascendant does not.",
      ],
    },
    {
      h2: "What to do when the poruthams do not agree",
      bullets: [
        "Find out which ones failed. A Rajju mismatch and a Vedha mismatch are very different situations with the same headline count.",
        "Check whether an exception applies. Several poruthams have recognised conditions that set them aside, and they are missed surprisingly often.",
        "Read the North Indian Ashtakoot alongside it. The two systems weight things differently, and a couple weak on one can be strong on the other.",
        "Look at Mangal dosha and the current dasha periods, which porutham does not cover at all.",
        "Take remedies proportionately: worth doing where a factor is genuinely afflicted, worth skipping where it is not.",
      ],
    },
    {
      h2: "Check your match with Aura AI",
      bullets: [
        "Both charts computed from precise astronomy, so the nakshatra, pada and rasi are exact.",
        "The full Ashtakoot gun milan scored across all eight kootas, several of which are the direct counterparts of Rasi, Rasi Athipathi, Vasya, Yoni and Ganam.",
        "Mangal, Nadi and Bhakoot dosha checked in both charts, including the classical cancellation rules.",
        "A plain-language reading of what the result means, and the chance to ask follow-up questions about your own charts.",
        "Free to run, private, and available whenever you need it.",
      ],
    },
  ],
  faqs: [
    {
      q: "What is thirumana porutham?",
      a: "Thirumana porutham is the Tamil system of marriage matching. It compares two birth charts across ten factors read from the birth nakshatra and rasi, covering health, temperament, progeny, intimacy, family standing and the longevity of the marriage.",
    },
    {
      q: "How many poruthams should match for marriage?",
      a: "There is no fixed pass mark the way there is with 18 out of 36 gunas in the North Indian system. Most families look for a majority of the ten to agree, and pay particular attention to Rajju, Dinam, Ganam and Yoni. Practice varies by family and region.",
    },
    {
      q: "Is porutham the same as kundli matching?",
      a: "No. Kundli matching (Ashtakoot gun milan) is the North Indian method: eight kootas scored out of 36 gunas. Porutham is the Tamil method: ten factors read from nakshatra and rasi, producing a list of agreements rather than a total. They can reach different conclusions about the same couple.",
    },
    {
      q: "What is Rajju porutham?",
      a: "Rajju divides the 27 nakshatras into five groups tied to parts of the body: foot, thigh, waist, neck and head. When both partners fall in the same group the porutham does not agree. It is read for the longevity of the marriage and is usually treated as the most serious of the ten.",
    },
    {
      q: "What is Vedha porutham?",
      a: "Vedha checks for affliction between the two birth nakshatras. Certain star pairs are considered to obstruct each other, and where that applies the porutham does not agree. It is generally weighed after the heavier factors.",
    },
    {
      q: "Can porutham be checked by name?",
      a: "It can, by inferring a likely nakshatra from the first syllable of each name, and that is a genuine traditional convention. But it approximates one input rather than measuring it. Checking by date, exact time and place of birth is far more accurate.",
    },
    {
      q: "Do I need the exact birth time?",
      a: "An exact time gives the most accurate result. Most poruntham factors come from the nakshatra and rasi, which usually hold steady across a few hours, so a good estimate still produces a useful check you can refine later.",
    },
    {
      q: "Is this free?",
      a: "Yes. You can run a full compatibility check and ask your first question free, with no card required. Detailed written reports are available on affordable plans.",
    },
  ],
  schemaName: "Thirumana Porutham - Aura AI",
  schemaDescription:
    "Free thirumana porutham marriage matching: all 10 poruthams explained, read from birth nakshatra and rasi, with Ashtakoot gun milan and dosha checks by AI.",
  finalHeading: "Check the match, then ask what it means",
  finalText:
    "Run the compatibility check free, then ask Aura AI what the result means for the two of you. The first question costs nothing.",
  disclaimer:
    "Porutham is a tradition of interpretation, not a prediction of any outcome. Nothing here is a substitute for your own judgement, or for professional advice on health, legal or financial matters.",
};

/* ---------------------------------------------------------------------------
   PAID-ADS LANDING PAGE: /free-kundli-matching

   NOT an SEO page, and it must never become one. It exists so marketing has a
   kundli-matching page they can rewrite at will without touching the one that
   ranks. The copy is the pre-2026-09-11 /kundli-matching page, restored from
   git (commit 9034468) because that shorter version is what they asked for.

   It is kept OUT of the index by omission, which is how IndexPolicy works:
   /free-kundli-matching is absent from INDEXABLE in IndexPolicy.tsx and from
   ALLOW in public/index.html, so the bootstrap script stamps noindex before
   the bundle loads and the prerendered file carries it too. It is also absent
   from ROUTES in scripts/gen-sitemap.js, and nothing links to it.

   DO NOT add this route to any of those three lists, and do not link to it
   from the nav or footer. Two near-identical matching pages competing in the
   index is exactly what this page was created to avoid.

   It IS in reactSnap.include and scripts/prerender.js, which is deliberate:
   prerendering costs nothing and paid traffic gets a fast first paint.

   The tool is the CURRENT <KundliMatch />, not the old one. Only the copy was
   rolled back; ads traffic should get the better-converting tool.
--------------------------------------------------------------------------- */
export const freeKundliMatchingAds: LandingConfig = {
  url: "https://astroaura.ai/free-kundli-matching",
  title: "Free Kundli Matching for Marriage, Gun Milan | Aura AI",
  description:
    "Free kundli matching (gun milan) for marriage. Aura AI checks the 36 gunas, Mangal dosha and Nadi to give a clear, honest compatibility score for two birth charts.",
  eyebrow: "Kundli Matching, made clear",
  h1Pre: "Free",
  h1Accent: "Kundli Matching",
  h1Post: " for Marriage",
  sub: "Match two kundli charts in minutes. Aura AI runs full Ashtakoot gun milan, checks Mangal dosha and Nadi, and explains what the score actually means for your relationship, in plain language.",
  primaryCta: "Match kundli free",
  secondaryCta: "Ask about compatibility",
  sections: [
    {
      h2: "What is kundli matching (gun milan)?",
      body: [
        "Kundli matching, also called gun milan or horoscope matching, compares the birth charts of two people to judge their compatibility for marriage. The classical Vedic method is Ashtakoot, which scores eight factors for a total of 36 gunas.",
        "A higher score suggests stronger natural compatibility, but the number alone is not the whole story. Aura AI reads the full picture, including doshas, and tells you clearly what to watch for and what to celebrate.",
      ],
    },
    {
      h2: "How kundli matching works here",
      steps: [
        "Enter both partners' birth details (date, time and place).",
        "Aura AI computes both charts and runs the 36-guna Ashtakoot analysis.",
        "It checks Mangal dosha (Manglik), Nadi dosha and other key factors.",
        "You get a clear compatibility score plus an honest, plain-language reading.",
      ],
    },
    {
      h2: "What we check in a match",
      bullets: [
        "The 36 gunas across all eight Ashtakoot factors.",
        "Mangal dosha (Manglik status) for both partners and how to reconcile it.",
        "Nadi and Bhakoot compatibility, the factors that most affect harmony.",
        "Strengths, risks and remedies, so you make an informed decision.",
      ],
    },
    {
      h2: "Why match with AI",
      bullets: [
        "Reads 100+ astrological variables at once, more than any single astrologer.",
        "Consistent, transparent scoring, with the reasoning shown, not hidden.",
        "Combines thousands of classical Jyotish rules into one calm answer.",
        "Free to start, private, and available whenever you need it.",
      ],
    },
  ],
  faqs: [
    {
      q: "Is kundli matching free?",
      a: "Yes. You can run a full gun milan compatibility check and ask your first question free. Detailed reports are available on affordable plans.",
    },
    {
      q: "How many gunas are needed for marriage?",
      a: "Traditionally 18 or more out of 36 is considered acceptable, and higher is better. But the score is a guide, not a verdict. Aura AI explains the full context, including any doshas, so you understand the real compatibility.",
    },
    {
      q: "What if one partner is Manglik?",
      a: "Being Manglik (Mangal dosha) is common and usually manageable. Aura AI checks both charts, tells you whether the dosha is cancelled or balanced, and suggests remedies where relevant.",
    },
    {
      q: "Do I need exact birth times for both people?",
      a: "For the most accurate match, yes, because doshas and several gunas depend on birth time. Best estimates still give useful results you can refine later.",
    },
  ],
  schemaName: "Kundli Matching - Aura AI",
  schemaDescription:
    "Free kundli matching and gun milan for marriage: 36-guna Ashtakoot analysis, Mangal dosha and Nadi checks, with a clear compatibility score explained by AI.",
  finalHeading: "Your first question is free",
  finalText:
    "Match the charts, then ask Aura AI what the score actually means for the two of you. The first question costs nothing.",
};

// AI Jyotish (Hinglish) ----------------------------------------------------
// BUILD 2026-10-13 (item 23): the jyotish cluster (94 queries, ~21K imp over
// 90d) had NO page. GSC 28d: "ai jyotish" 98 clicks / 3,021 imp @6.8 with
// 3.2% CTR, held by the HOMEPAGE (97 / 3,008; /ai-astrologer: 1 / 33 @10.9);
// "ai jyotish free" 72 / 1,791 @7.2; "ai jyotish free chat" 12 / 310; "ai
// jyotish chat" 6 / 40 @4.8 (15%); "free ai jyotish app" 5 / 38 @1.9 (13.2%).
// The searcher uses the Hindi word, so the page speaks Hinglish. Item 18 left
// only additive jyotish signals on /ai-astrologer; the head belongs here.
export const aiJyotish: LandingConfig = {
  url: "https://astroaura.ai/ai-jyotish",
  title: "AI Jyotish Online: Apni Kundli Se Seedha Poochho | Aura AI",
  description:
    "AI jyotish free chat, Hindi ya English mein. Aura AI aapki kundli bana kar graha, dasha aur yog padhta hai, aur career, shaadi, paise pe seedha jawaab deta hai.",
  eyebrow: "AI Jyotish, Hindi mein",
  h1Pre: "AI",
  h1Accent: "Jyotish",
  h1Post: ", Seedha Jawaab",
  sub: "Apne sawaal apni kundli se poochho. Aura AI ek AI jyotish hai jo aapke graha, dasha aur yog classical Vedic tareeke se padhta hai, aur seedha jawaab deta hai, Hindi ya English mein. Pehla sawaal free.",
  primaryCta: "Pehla sawaal free poochho",
  secondaryCta: "Free kundli banao",
  schemaName: "AI Jyotish Free Chat - Aura AI",
  schemaDescription:
    "Free AI jyotish chat in Hindi or English. Kundli-based answers on career, marriage, money and timing from classical Vedic astrology.",
  sections: [
    {
      h2: "AI jyotish kya hai?",
      body: [
        "Jyotish, yaani Vedic astrology. AI jyotish ek software hai jo wahi kaam karta hai jo ek jyotishi kundli dekh kar karta hai: aapki date, time aur place of birth se Swiss Ephemeris astronomy data ke saath exact graha positions nikalta hai, phir unhe classical Jyotish ke niyamon se padhta hai.",
        "Farak sirf itna hai ki jawaab seconds mein milta hai, kabhi bhi, aur pehla sawaal free hai. Koi appointment nahi, koi per-minute meter nahi.",
      ],
    },
    {
      h2: "AI jyotish free chat kaise kaam karta hai",
      body: [
        "Apni date, time aur place of birth daalo. Aura AI turant aapki poori kundli banata hai: lagna, graha, nakshatra, dasha timeline aur yog.",
        "Phir chat mein apna sawaal poochho, bilkul waise jaise kisi jyotishi se poochte: naukri, shaadi, paisa, health, ya koi bada faisla. Jawaab aapki apni kundli se aata hai, kisi generic rashifal se nahi.",
      ],
    },
    {
      h2: "Kya poochh sakte ho",
      bullets: [
        "Meri naukri ya business kab aage badhega?",
        "Shaadi kab hogi, aur kundli milan kya kehta hai?",
        "Kaunsa mahadasha chal raha hai aur iska matlab kya hai?",
        "Main manglik hoon, iska asar aur parihar kya hai?",
        "Bada faisla lene ka sahi samay kaunsa hai?",
      ],
    },
    {
      h2: "Browser mein ya app mein, dono free start",
      body: [
        "AI jyotish seedha browser mein chalta hai, kuch download karne ki zaroorat nahi. Android app bhi hai, wahi kundli, wahi chat.",
        "Dono jagah pehla sawaal free hai, bina card ke. Uske baad chats wallet credits se chalti hain, aur regular consult karne waalon ke liye optional subscription bhi hai. Koi hidden fees nahi, aur wallet me kuch apne aap auto-renew nahi hota.",
      ],
    },
    {
      h2: "AI jyotish vs pandit ji",
      body: [
        "Ek achhe jyotishi ke paas experience aur intuition hota hai. AI jyotish ke paas speed, consistency aur availability: har baar wahi careful calculation, koi waiting nahi, koi badhti fees nahi, aur raat ke do baje bhi jawaab.",
        "Aura AI roz ke sawaalon ke liye hai. Jab aapko lagta hai ki baat gehri hai, toh wo aapko batata hai ki chart mein kya hai aur kahan aur dekhna chahiye.",
      ],
    },
  ],
  faqs: [
    {
      q: "Kya AI jyotish free hai?",
      a: "Pehla sawaal bilkul free hai, bina card ke. Free kundli aur birth chart bhi. Uske baad chats wallet credits se chalti hain, jitna use karo utna hi, koi subscription nahi.",
    },
    {
      q: "AI jyotish kitna accurate hai?",
      a: "Kundli Swiss Ephemeris astronomy data se banti hai, wahi jo professional jyotishi use karte hain. Interpretation classical Vedic texts par aadharit hai. Ye favourable windows dikhata hai, fixed dates nahi, kyunki Vedic timing aise hi kaam karti hai.",
    },
    {
      q: "Kya ye Hindi mein baat karta hai?",
      a: "Haan. Hindi ya English, jo aapko comfortable lage. Sawaal Hinglish mein bhi poochh sakte ho.",
    },
    {
      q: "Kya AI jyotish meri kundli bana sakta hai?",
      a: "Haan. Date, time aur place of birth daalte hi poori kundli ban jaati hai: lagna, graha, nakshatra, dasha timeline aur yog, sab turant aur free.",
    },
    {
      q: "Is an AI jyotish the same as an AI astrologer?",
      a: "Yes. Jyotish is the Sanskrit name for Vedic astrology, so an AI jyotish and an AI Vedic astrologer are the same thing. Aura AI reads your kundli the classical way and chats in Hindi or English.",
    },
  ],
  finalHeading: "Apna pehla sawaal poochho",
  finalText:
    "Aura AI aapki kundli se jawaab deta hai, Hindi ya English mein. Pehla sawaal free.",
  relatedLinks: [
    { to: "/free-kundli", label: "Free Kundli" },
    { to: "/ai-astrologer", label: "AI Astrologer" },
    { to: "/kundli-matching", label: "Kundli Matching" },
    { to: "/mahadasha-calculator", label: "Mahadasha Calculator" },
  ],
};

// --- /hi: the real Hindi page ---------------------------------------------
// Item 32. The old language landings (/kn /gu /bn /ar) mount the English
// homepage and canonicalise to "/", so the site had no actual Hindi URL.
// This page is written in Devanagari for the Hindi query space (ai jyotish
// hindi, muft kundli, kundli milan, aaj ka rashifal) and pairs with "/"
// through the bilingual hreflang cluster in IndexPolicy. lang: "hi" drives
// the container lang attribute and schema inLanguage via SeoLandingPage.
export const hindi: LandingConfig = {
  url: "https://astroaura.ai/hi",
  title: "AI ज्योतिष हिंदी में: मुफ़्त कुंडली और कुंडली मिलान | Aura AI",
  description:
    "हिंदी में AI ज्योतिष। मुफ़्त जन्म कुंडली बनाइए, कुंडली मिलान कीजिए और अपनी कुंडली से करियर, शादी और पैसे के सवाल पूछिए। पहला सवाल मुफ़्त।",
  lang: "hi",
  wide: true,
  eyebrow: "हिंदी में ज्योतिष",
  h1Pre: "AI",
  h1Accent: "ज्योतिष",
  h1Post: ", अब हिंदी में",
  sub: "अपनी जन्म कुंडली से सीधे सवाल पूछिए, हिंदी में। Aura AI आपकी तारीख़, समय और जन्म स्थान से सटीक कुंडली बनाता है और शास्त्रीय ज्योतिष के नियमों से पढ़कर सीधा जवाब देता है। पहला सवाल मुफ़्त।",
  primaryCta: "पहला सवाल मुफ़्त पूछिए",
  secondaryCta: "मुफ़्त कुंडली बनाइए",
  schemaName: "AI Jyotish in Hindi - Aura AI",
  schemaDescription:
    "AI astrology in Hindi: free janam kundli, kundli milan and chart-based answers on career, marriage and money from classical Vedic astrology.",
  areaServed: ["IN"],
  priceCurrency: "INR",
  lede: [
    "ज्योतिषी के पास जाने के लिए अपॉइंटमेंट, फ़ीस और सही सवाल, तीनों चाहिए। यहाँ सिर्फ़ जन्म की तारीख़, समय और जगह चाहिए। Aura AI उसी से आपकी पूरी जन्म कुंडली बनाता है, लग्न, ग्रह, नक्षत्र और दशा के साथ, और फिर आप उससे हिंदी में बात कर सकते हैं, जैसे किसी जानकार से करते हैं।",
    "कुंडली मुफ़्त बनती है, कुंडली मिलान मुफ़्त है और चैट का पहला सवाल भी मुफ़्त है। कोई सब्सक्रिप्शन नहीं, कुछ अपने आप रिन्यू नहीं होता।",
  ],
  heroFacts: [
    "मुफ़्त जन्म कुंडली",
    "36 गुण कुंडली मिलान",
    "हिंदी में जवाब",
    "पहला सवाल मुफ़्त",
  ],
  sections: [
    {
      h2: "AI ज्योतिष क्या है?",
      body: [
        "AI ज्योतिष वही काम करता है जो एक ज्योतिषी कुंडली देखकर करता है: आपके जन्म विवरण से ग्रहों की सटीक स्थिति निकालना और उसे शास्त्रीय ज्योतिष के नियमों से पढ़ना। फ़र्क़ यह है कि गणना Swiss Ephemeris के खगोलीय आँकड़ों से होती है, इसलिए ग्रहों की डिग्री का अंदाज़ा नहीं लगाया जाता, वह गणित से निकलती है।",
        "जवाब आपकी अपनी कुंडली से आता है, किसी बने-बनाए राशिफल से नहीं। आप जब चाहें पूछ सकते हैं, आधी रात को भी, और पहला सवाल मुफ़्त है।",
      ],
    },
    {
      h2: "यहाँ क्या-क्या मिलता है",
      bullets: [
        "मुफ़्त जन्म कुंडली: लग्न, ग्रह, नक्षत्र और महादशा की पूरी टाइमलाइन",
        "कुंडली मिलान: 36 गुणों का मिलान, नाड़ी और भकूट दोष की जाँच के साथ",
        "मांगलिक दोष, काल सर्प दोष और साढ़े साती की जाँच, कारण समेत",
        "आज का राशिफल और पंचांग, शुभ मुहूर्त के साथ",
        "AI ज्योतिषी से चैट: करियर, शादी, पैसा, या कोई भी बड़ा फ़ैसला",
      ],
    },
    {
      h2: "कैसे काम करता है",
      steps: [
        "अपनी जन्म तारीख़, सही समय और जन्म स्थान डालिए।",
        "Aura AI तुरंत आपकी कुंडली बनाता है, खगोलीय गणना से, अंदाज़े से नहीं।",
        "फिर चैट में अपना सवाल हिंदी में पूछिए। जवाब आपके ग्रहों, भावों और चल रही दशा से आता है।",
      ],
    },
    {
      h2: "हिंदी में पूछिए, हिंदी में जवाब",
      body: [
        "सवाल वैसे ही पूछिए जैसे आप बोलते हैं: नौकरी कब बदलेगी? शादी कब होगी? कौन सी महादशा चल रही है और उसका मतलब क्या है? मैं मांगलिक हूँ या नहीं?",
        "जवाब सीधी हिंदी में मिलता है, बिना भारी शब्दावली के। जहाँ शास्त्रीय शब्द ज़रूरी है, वहाँ उसका मतलब भी साथ में समझाया जाता है।",
      ],
      chips: [
        { to: "/ai-jyotish", label: "AI ज्योतिष फ्री चैट" },
        { to: "/free-kundli", label: "मुफ़्त कुंडली" },
        { to: "/kundli-matching", label: "कुंडली मिलान" },
        { to: "/daily-horoscope", label: "आज का राशिफल" },
      ],
    },
    {
      h2: "12 राशियों का राशिफल, हिंदी में",
      body: [
        "हर राशि का अपना पेज है: स्वभाव, करियर, प्रेम, शुभ रंग, रत्न और नक्षत्र, सब हिंदी में। अपनी राशि चुनिए, और आज का राशिफल डेली होरोस्कोप पेज पर रोज़ ताज़ा मिलता है।",
      ],
      chips: [
        { to: "/mesh-rashifal", label: "मेष" },
        { to: "/vrishabh-rashifal", label: "वृषभ" },
        { to: "/mithun-rashifal", label: "मिथुन" },
        { to: "/kark-rashifal", label: "कर्क" },
        { to: "/singh-rashifal", label: "सिंह" },
        { to: "/kanya-rashifal", label: "कन्या" },
        { to: "/tula-rashifal", label: "तुला" },
        { to: "/vrishchik-rashifal", label: "वृश्चिक" },
        { to: "/dhanu-rashifal", label: "धनु" },
        { to: "/makar-rashifal", label: "मकर" },
        { to: "/kumbh-rashifal", label: "कुंभ" },
        { to: "/meen-rashifal", label: "मीन" },
      ],
    },
    {
      h2: "मुफ़्त जन्म कुंडली में क्या-क्या मिलता है",
      body: [
        "जन्म की तारीख़, सही समय और जगह से पूरी कुंडली बनती है। हर हिस्सा क्या बताता है, एक नज़र में:",
      ],
      table: {
        head: ["कुंडली का हिस्सा", "क्या बताता है"],
        rows: [
          ["लग्न", "जन्म के समय पूर्व क्षितिज पर चढ़ती राशि: व्यक्तित्व और पूरी कुंडली का आधार"],
          ["चंद्र राशि और नक्षत्र", "मन का स्वभाव: राशिफल और कुंडली मिलान इसी से होता है"],
          ["ग्रह स्थिति", "सभी नौ ग्रह किस राशि और किस भाव में हैं, डिग्री के साथ"],
          ["विंशोत्तरी महादशा", "कौन सा ग्रह-काल चल रहा है और आगे कौन सा आएगा, तारीख़ों समेत"],
          ["दोष जाँच", "मांगलिक, काल सर्प और साढ़े साती: दोष है या नहीं, और क्यों"],
        ],
        caption: "मुफ़्त कुंडली के हिस्से और उनका मतलब",
      },
      chips: [
        { to: "/free-kundli", label: "मुफ़्त कुंडली बनाइए" },
        { to: "/nakshatra-calculator", label: "नक्षत्र कैलकुलेटर" },
        { to: "/moon-sign-calculator", label: "चंद्र राशि कैलकुलेटर" },
      ],
    },
    {
      h2: "भरोसे की बात",
      body: [
        "हम डर नहीं बेचते। कोई दोष मिलने पर पेज आपको महँगे उपाय की ओर नहीं धकेलता, पहले यह बताता है कि वह दोष बनता कैसे है और शास्त्रों में उसके कौन से अपवाद हैं।",
        "गणना हर बार एक जैसी होती है: वही जन्म विवरण, वही कुंडली। ज्योतिष को हम आत्म-चिंतन और समय समझने का एक नज़रिया मानते हैं, आपके फ़ैसलों का मालिक नहीं।",
      ],
    },
  ],
  faqs: [
    {
      q: "क्या कुंडली सच में मुफ़्त बनती है?",
      a: "हाँ। जन्म कुंडली बनाना, उसे देखना और कुंडली मिलान, दोनों मुफ़्त हैं। चैट का पहला सवाल भी मुफ़्त है, उसके बाद की चैट वॉलेट क्रेडिट से चलती है। कोई सब्सक्रिप्शन नहीं है।",
    },
    {
      q: "क्या AI ज्योतिष सटीक होता है?",
      a: "गणना की बात करें तो AI यहाँ इंसान से आगे है: ग्रहों की स्थिति Swiss Ephemeris के खगोलीय आँकड़ों से निकलती है, इसलिए लग्न, नक्षत्र और दशा हर बार सही और एक जैसी आती है। व्याख्या शास्त्रीय नियमों से होती है। भविष्य कोई भी तरीक़ा पक्का नहीं बता सकता, इसलिए हम जवाब को कुंडली से जोड़कर समझाते हैं, दावा करके नहीं।",
    },
    {
      q: "कुंडली मिलान कैसे होता है?",
      a: "दोनों के जन्म विवरण से अष्टकूट मिलान होता है: 36 गुणों का स्कोर, नाड़ी और भकूट दोष की जाँच, और मंगल दोष की अलग से जाँच। स्कोर के साथ यह भी समझाया जाता है कि कौन सा कूट क्या मापता है, ताकि आप सिर्फ़ अंक पर अटके न रहें।",
    },
    {
      q: "क्या मैं पूरी बात हिंदी में कर सकता हूँ?",
      a: "हाँ। सवाल हिंदी में पूछिए, जवाब हिंदी में मिलेगा। English में भी पूछ सकते हैं, और Hinglish में भी, जैसे आपको सहज लगे।",
    },
    {
      q: "पहले सवाल के बाद क्या होता है?",
      a: "पहला सवाल हर जगह मुफ़्त है, ब्राउज़र में भी और Android app में भी, बिना कार्ड के। उसके बाद चैट वॉलेट क्रेडिट से चलती है: जितना पूछना हो उतना रिचार्ज कीजिए, कुछ अपने आप नहीं कटता।",
    },
    {
      q: "कुंडली बनाने के लिए क्या-क्या जानकारी चाहिए?",
      a: "सिर्फ़ तीन चीज़ें: जन्म की तारीख़, जन्म का समय और जन्म की जगह। समय जितना सही होगा, लग्न और भाव उतने सटीक बनेंगे। समय बिल्कुल पता न हो तो भी चंद्र राशि और नक्षत्र ज़्यादातर मामलों में निकल आते हैं।",
    },
    {
      q: "आज का राशिफल हिंदी में कहाँ मिलेगा?",
      a: "डेली होरोस्कोप पेज पर हर दिन का राशिफल ताज़ा बनता है। हर राशि का स्थायी परिचय, स्वभाव, करियर, प्रेम, शुभ रंग और रत्न, उसके अपने राशिफल पेज पर है, जैसे मेष राशिफल या सिंह राशिफल।",
    },
    {
      q: "Aura AI app कहाँ मिलेगा?",
      a: "Android के लिए Google Play पर Aura AI app है, और ब्राउज़र में astroaura.ai पर सब कुछ बिना app के भी चलता है: कुंडली, मिलान, राशिफल और चैट, सब हिंदी में।",
    },
  ],
  finalHeading: "अपनी कुंडली से पूछिए",
  finalText:
    "मुफ़्त कुंडली बनाइए और पहला सवाल हिंदी में पूछिए। जवाब आपके अपने ग्रहों से आएगा।",
  relatedLinks: [
    { to: "/manglik-calculator", label: "मांगलिक दोष जाँच" },
    { to: "/mesh-rashifal", label: "मेष राशिफल" },
    { to: "/free-astrology", label: "सभी मुफ़्त टूल" },
    { to: "/blog/chatgpt-se-kundli", label: "ChatGPT se kundli: sach kya hai" },
  ],
  disclaimer:
    "Aura AI ज्योतिषीय मार्गदर्शन आत्म-चिंतन और समझ के लिए देता है। यह चिकित्सा, क़ानूनी या वित्तीय सलाह नहीं है, और कोई भी बड़ा फ़ैसला सिर्फ़ कुंडली के आधार पर नहीं लेना चाहिए।",
};

// --- /free-astrology: the tools hub ----------------------------------------
// Item 33. One page that lists every free tool on the site, grouped. It
// targets "free astrology tools" / "free astrology online" long-tails, NOT
// the bare "free astrology" head term, which the homepage ranks for; the
// title is worded to support the homepage, not compete with it. Every chip
// is an in-content link, so this page also feeds link equity to each tool.
export const freeAstrology: LandingConfig = {
  url: "https://astroaura.ai/free-astrology",
  title: "Free Astrology: Kundli, Matching, Horoscope & Chat | Aura AI",
  description:
    "Every free astrology tool in one place: free kundli, kundli matching, manglik and sade sati calculators, daily horoscope, panchang, rashifal in Hindi and AI astrologer chat.",
  wide: true,
  eyebrow: "All free tools",
  h1Pre: "Free",
  h1Accent: "Astrology",
  h1Post: " Tools",
  sub: "Everything on Aura AI you can use without paying: kundli, matching, dosha checks, horoscopes and the AI astrologer's first question. All of it computed from a real ephemeris, none of it behind a signup wall.",
  primaryCta: "Ask your first question free",
  secondaryCta: "Make your free kundli",
  schemaName: "Free Astrology Tools - Aura AI",
  schemaDescription:
    "Directory of Aura AI's free astrology tools: kundli generator, kundli matching, dosha calculators, daily horoscope, panchang, Hindi rashifal pages and AI astrologer chat.",
  lede: [
    "Free astrology online usually means a teaser that ends at a paywall. This page is the honest version: every tool below is genuinely free to use, the charts are computed with Swiss Ephemeris astronomy data, and the only paid thing on the site is continuing a chat beyond your free first question.",
    "Start with your kundli if you are new. Everything else, matching, doshas, dashas, horoscopes, builds on it.",
  ],
  sections: [
    {
      h2: "Kundli and matching",
      body: [
        "The free kundli is the foundation: lagna, planets, nakshatras and the full Vimshottari dasha timeline from your birth details. Matching compares two charts the classical way, 36 gunas with Nadi and Bhakoot checked, and mangal dosha examined separately, which is how it is done properly.",
      ],
      chips: [
        { to: "/free-kundli", label: "Free Kundli" },
        { to: "/kundli-matching", label: "Kundli Matching" },
        { to: "/free-kundli-matching", label: "Free Kundli Matching" },
        { to: "/thirumana-porutham", label: "Thirumana Porutham" },
        { to: "/love-calculator", label: "Love Calculator" },
      ],
    },
    {
      h2: "Dosha and dasha calculators",
      body: [
        "Each calculator tells you whether the condition exists in your chart, why it arises, and which classical cancellations apply, before anyone mentions a remedy. That order matters: most manglik panic, for instance, dissolves on reading the actual rule.",
      ],
      chips: [
        { to: "/manglik-calculator", label: "Manglik Dosha Calculator" },
        { to: "/kaal-sarp-dosha", label: "Kaal Sarp Dosha" },
        { to: "/sade-sati-calculator", label: "Sade Sati Calculator" },
        { to: "/mahadasha-calculator", label: "Mahadasha Calculator" },
      ],
    },
    {
      h2: "Sign and chart calculators",
      body: [
        "Most people know their sun sign; Vedic astrology runs on the Moon sign, the rising sign and the nakshatra. These three tell you which one is actually yours.",
      ],
      chips: [
        { to: "/moon-sign-calculator", label: "Moon Sign Calculator" },
        { to: "/nakshatra-calculator", label: "Nakshatra Calculator" },
        { to: "/rising-sign-calculator", label: "Rising Sign Calculator" },
      ],
    },
    {
      h2: "Horoscope, panchang and muhurat",
      body: [
        "The daily horoscope is written fresh each day. The panchang gives today's tithi, nakshatra and yoga, and the muhurat page the auspicious windows, the two things people actually open a panchang for.",
      ],
      chips: [
        { to: "/daily-horoscope", label: "Daily Horoscope" },
        { to: "/horoscope", label: "AI Horoscope" },
        { to: "/panchang", label: "Panchang" },
        { to: "/muhurat", label: "Muhurat" },
      ],
    },
    {
      h2: "राशिफल, हिंदी में",
      body: [
        "Each sign has its own rashifal page in Hindi: the sign's nature, career, love and marriage, with the daily reading one tap away. The full Hindi experience lives on the Hindi page.",
      ],
      chips: [
        { to: "/hi", label: "हिंदी में ज्योतिष" },
        { to: "/mesh-rashifal", label: "मेष" },
        { to: "/vrishabh-rashifal", label: "वृषभ" },
        { to: "/mithun-rashifal", label: "मिथुन" },
        { to: "/kark-rashifal", label: "कर्क" },
        { to: "/singh-rashifal", label: "सिंह" },
        { to: "/kanya-rashifal", label: "कन्या" },
        { to: "/tula-rashifal", label: "तुला" },
        { to: "/vrishchik-rashifal", label: "वृश्चिक" },
        { to: "/dhanu-rashifal", label: "धनु" },
        { to: "/makar-rashifal", label: "मकर" },
        { to: "/kumbh-rashifal", label: "कुंभ" },
        { to: "/meen-rashifal", label: "मीन" },
      ],
    },
    {
      h2: "Talk to the AI astrologer",
      body: [
        "When a calculator's one-line answer is not enough, ask the question properly. The AI astrologer reads your computed chart, your houses, your running dasha, and answers in English or Hindi. The first question costs nothing.",
      ],
      chips: [
        { to: "/ai-astrologer", label: "AI Astrologer" },
        { to: "/talk-to-astrologer", label: "Free Astrology Chat" },
        { to: "/ai-jyotish", label: "AI Jyotish (Hindi)" },
      ],
    },
    {
      h2: "Which tool answers which question",
      body: [
        "If you know the question but not the tool, start here.",
      ],
      table: {
        head: ["Your question", "The free tool for it"],
        rows: [
          ["Will our marriage match work?", "Kundli Matching: 36 gunas with Nadi and Bhakoot checked"],
          ["Am I manglik?", "Manglik Dosha Calculator, with the cancellation rules applied"],
          ["Which mahadasha am I running?", "Mahadasha Calculator: the full Vimshottari timeline"],
          ["Is Sade Sati going on for me?", "Sade Sati Calculator, from your Moon sign"],
          ["What is my Moon sign or nakshatra?", "Moon Sign and Nakshatra Calculators"],
          ["What does today look like?", "Daily Horoscope, with Panchang and Muhurat for timings"],
          ["Something none of these cover", "Ask the AI astrologer: first question free"],
        ],
        caption: "Every tool is free and starts from a real computed chart",
      },
    },
  ],
  faqs: [
    {
      q: "Is astrology on Aura AI really free?",
      a: "The kundli, matching, every calculator, the daily horoscope and panchang are fully free, no signup wall. Chat with the AI astrologer starts free too: the first question costs nothing, and only continuing beyond it uses wallet credits. There is no subscription and nothing auto-renews.",
    },
    {
      q: "How accurate are free astrology calculators?",
      a: "As accurate as their astronomy. Every tool here computes planetary positions from Swiss Ephemeris data, the same source professional software uses, so the same birth details always give the same chart. The interpretation layer follows classical Jyotish rules, and our methodology page spells out exactly what we do and do not claim.",
    },
    {
      q: "Which tool should I start with?",
      a: "The free kundli. It computes your lagna, Moon sign, nakshatra and dasha timeline, which every other tool builds on. After that, go wherever your question lives: matching for marriage, the dosha calculators for a specific worry, or the chat for anything the calculators cannot cover.",
    },
    {
      q: "Do these tools work in Hindi?",
      a: "Yes. The rashifal pages are written in Hindi, the Hindi page collects the full experience, and the AI astrologer chats comfortably in Hindi, English or Hinglish.",
    },
  ],
  finalHeading: "Start with your chart",
  finalText:
    "Make your free kundli, check what you came to check, and ask your first question free. Everything here starts from your own chart, not a generic forecast.",
  relatedLinks: [
    { to: "/how-it-works", label: "How It Works" },
    { to: "/methodology", label: "Our Methodology" },
  ],
};
